import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ACCOUNT_KIND_MAP,
  ACTIVITY_TYPE_MAP,
  CONTRACT_STATUS_MAP,
  DEAL_RESULT_MAP,
  DEAL_STAGE_MAP,
  INVOICE_STATUS_MAP,
  LEAD_RATING_DEFS,
  PROJECT_COLORS,
  renderMentions,
  whatsappNumber,
  type FeedbackView,
} from "@goyone/shared";
import { api, qs, upload } from "../../lib/api";
import { date, dateTime, money, relative } from "../../lib/format";
import { Icon } from "../../components/Icon";
import { FxRateField } from "../../components/FxRateField";
import { currencySymbol, isForeignCurrency, toBase } from "../../lib/currency";
import {
  Badge,
  ErrorState,
  Modal,
  PageHead,
  SearchBox,
} from "../../components/ui";
import { RecordStats, type RecordStat } from "../../components/RecordStats";
import { CardPickerButton, useCardChoice } from "../../components/CardPicker";
import {
  LayoutColumn,
  ProfileLayoutBar,
  ProfileLayoutButton,
  ProfileLayoutProvider,
  Section,
  useProfileLayout,
  type SectionDef,
} from "../../components/ProfileLayout";
import { Attachments } from "../../components/Attachments";
import { Avatar, OwnerChip, useTeamDirectory } from "../../components/People";
import { RecordTimeline } from "../../components/RecordTimeline";
import { Feedback } from "../../components/Feedback";
import { MailCompose } from "../../components/MailCompose";
import {
  QuickMessageModal,
  type QuickChannel,
} from "../../components/QuickMessage";
import { QuickCallModal } from "../../components/QuickCall";
import { ChannelLogo } from "../../components/ChannelLogo";
import { useToast } from "../../components/Toast";
import { RatingLinkButton } from "../../components/RatingRequest";
import { AccountRatings } from "../../components/AccountRatings";
import { TimerButton } from "../../components/WorkTimer";
import { TimeLog } from "../../components/WorkTimer";
import { AccountQuickActions } from "./AccountQuickActions";
import { CreditBar, useCredit } from "../../components/Credit";
import { useAuth } from "../../auth/AuthProvider";
import { issueInvoiceFromQuotation } from "../../lib/invoiceIssue";
import { ActivityHub, type HubActivity } from "./ActivityHub";
import { RatingMeter } from "../../components/RatingMeter";
import { BookingLinkButton } from "../../components/BookingLink";
import { DocPreview } from "../../components/DocPreview";
import { ReachIcons } from "../../components/Reach";
import {
  ContactDetailModal,
  ContactModal,
  type ContactRow,
} from "./ContactsList";
import { AccountEditor } from "./AccountEditor";
import { statementPath } from "../finance/PartyStatement";
import { ContractActions } from "../sales/ContractActions";
import { Menu } from "../../components/Menu";
import { DeleteButton } from "../../components/DeleteDialog";
import {
  CustomFieldList,
  activeFields,
  useCustomFields,
} from "../../components/CustomFields";

/** بيانات العميل المحتمل الأصلي — تبقى في ملف العميل بعد التحويل. */
interface OriginLead {
  id: string;
  name: string;
  company: string | null;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  altPhone: string | null;
  messengerHandle: string | null;
  instagramHandle: string | null;
  waPhone: string | null;
  smsPhone: string | null;
  mapsUrl: string | null;
  source: string | null;
  industry: string | null;
  city: string | null;
  address: string | null;
  notes: string | null;
  rating: "HOT" | "WARM" | "COLD";
  value: string | null;
  customFields: Record<string, unknown> | null;
  createdAt: string;
  convertedAt: string | null;
}

interface Profile {
  account: Record<string, any>;
  originLeads?: OriginLead[];
  contacts: Array<Record<string, any>>;
  deals: Array<Record<string, any>>;
  quotations: Array<Record<string, any>>;
  contracts: Array<Record<string, any>>;
  invoices: Array<Record<string, any>>;
  payments: Array<Record<string, any>>;
  activities: Array<Record<string, any>>;
  summary: {
    openDeals: number;
    wonDeals: number;
    openDealsValue: number;
    billed: number;
    collected: number;
    outstanding: number;
    openActivities: number;
    calls: number;
    meetings: number;
    mails: number;
    contactCount: number;
    /** كم عميلًا محتملًا تحوّل إلى هذا العميل — من أين جاء. */
    fromLeads: number;
    lastActivityAt: string | null;
    nextActionAt: string | null;
  };
}

/**
 * كل ما تعرفه شاشة العميل عنه.
 *
 * دالّةٌ لا ثابت: القيم تُقرأ من السجلّ في كل تصيير، والمُنتقي يختار منها
 * بالمفتاح.
 */
function accountCards(
  account: Record<string, any>,
  summary: Profile["summary"],
): RecordStat[] {
  const overdue =
    summary.nextActionAt !== null &&
    new Date(summary.nextActionAt).getTime() < Date.now();

  return [
    /* المسؤول أولًا: قبل الأرقام يأتي سؤال «من يتابع هذا العميل؟». */
    {
      key: "owner",
      label: "المسؤول",
      group: "المتابعة",
      icon: "user",
      color: "#3672EA",
      value: <OwnerChip ownerId={account.ownerId ?? null} size={26} />,
      note: account.ownerId ? undefined : "لم يُسنَد بعد",
      hint: "من يتابع هذا الحساب.",
    },
    {
      key: "lastActivity",
      label: "آخر نشاط",
      group: "المتابعة",
      icon: "clock",
      color: "#5596FF",
      value: summary.lastActivityAt
        ? relative(summary.lastActivityAt)
        : "لا شيء",
      note: summary.lastActivityAt
        ? date(summary.lastActivityAt)
        : "لم يُسجَّل نشاط",
      hint: "متى جرى آخر تعامل معه.",
    },
    {
      key: "nextAction",
      label: "النشاط القادم",
      group: "المتابعة",
      icon: "calendar",
      color: overdue ? "#DC2626" : "#51A521",
      value: summary.nextActionAt
        ? relative(summary.nextActionAt)
        : "لا شيء مجدول",
      note: summary.nextActionAt
        ? overdue
          ? "فات موعده"
          : date(summary.nextActionAt)
        : "جدول متابعة حتى لا يُنسى",
      hint: "أقرب موعد مخطَّط معه.",
    },
    {
      key: "openActivities",
      label: "أنشطة مفتوحة",
      group: "المتابعة",
      icon: "clipboard",
      color: summary.openActivities > 0 ? "#BF930B" : "#9BA3AE",
      value: summary.openActivities,
      numeric: true,
      note: summary.openActivities === 0 ? "لا شيء معلَّق" : "لم تُغلق بعد",
      hint: "ما خُطِّط ولم يُنجَز.",
    },
    {
      key: "contacts",
      label: "جهات الاتصال",
      group: "المتابعة",
      icon: "users",
      color: "#1D46A6",
      value: summary.contactCount,
      numeric: true,
      note:
        summary.fromLeads > 0
          ? `جاء من ${summary.fromLeads} عميل محتمل`
          : undefined,
      hint: "من نخاطبهم داخل هذه الجهة.",
    },

    {
      key: "openDeals",
      label: "صفقات مفتوحة",
      group: "المالية",
      icon: "funnel",
      color: "#3672EA",
      value: summary.openDeals,
      numeric: true,
      note: money(summary.openDealsValue),
      hint: "ما زال قيد التفاوض — وقيمتها المتوقّعة.",
    },
    {
      key: "wonDeals",
      label: "صفقات ناجحة",
      group: "المالية",
      icon: "check",
      color: "#51A521",
      value: summary.wonDeals,
      numeric: true,
      hint: "ما أُغلق بالفوز.",
    },
    {
      key: "billed",
      label: "إجمالي المفوتر",
      group: "المالية",
      icon: "receipt",
      color: "#1D46A6",
      value: money(summary.billed),
      numeric: true,
      hint: "مجموع ما صدرت به فواتير.",
    },
    {
      key: "collected",
      label: "المحصَّل",
      group: "المالية",
      icon: "wallet",
      color: "#51A521",
      value: money(summary.collected),
      numeric: true,
      hint: "ما دخل الخزينة فعلًا.",
    },
    {
      key: "outstanding",
      label: "الرصيد المستحقّ",
      group: "المالية",
      icon: "alert",
      color: summary.outstanding > 0 ? "#DC2626" : "#9BA3AE",
      value: (
        <span
          style={{
            color: summary.outstanding > 0 ? "var(--danger)" : undefined,
          }}
        >
          {money(summary.outstanding)}
        </span>
      ),
      numeric: true,
      note: summary.outstanding > 0 ? "لم يُحصَّل بعد" : "لا مستحقات",
      hint: "الفرق بين ما فُوتر وما حُصِّل.",
    },
    {
      key: "creditLimit",
      label: "حدّ الائتمان",
      group: "المالية",
      icon: "bank",
      color: "#8791A1",
      value: account.creditLimit ? money(account.creditLimit) : "بلا حدّ",
      numeric: !!account.creditLimit,
      note: account.creditLimit ? "أقصى ما يُسمح بتجاوزه" : "لم يُحدَّد",
      hint: "سقف المديونية المسموح به لهذا العميل.",
    },

    /*
     * صورة التعامل بعد الأرقام المالية.
     * «كم علينا وكم لنا» سؤال المحاسب، و«متى تكلّمنا معهم آخر مرة» سؤال من
     * سيرفع السماعة — وكلاهما يُفتح من هذه الشاشة.
     */
    {
      key: "touches",
      label: "التعامل",
      group: "التعامل",
      icon: "chat",
      color: "#1D46A6",
      value: `${summary.calls} / ${summary.meetings} / ${summary.mails}`,
      numeric: true,
      note: "مكالمات / اجتماعات / رسائل",
      hint: "مجمل ما جرى معه، موزَّعًا على القنوات.",
    },
    {
      key: "calls",
      label: "المكالمات",
      group: "التعامل",
      icon: "phone",
      color: "#51A521",
      value: summary.calls,
      numeric: true,
      hint: "عدد المكالمات المسجَّلة.",
    },
    {
      key: "meetings",
      label: "الاجتماعات",
      group: "التعامل",
      icon: "video",
      color: "#5596FF",
      value: summary.meetings,
      numeric: true,
      hint: "عدد الاجتماعات المسجَّلة.",
    },
    {
      key: "mails",
      label: "الرسائل",
      group: "التعامل",
      icon: "mail",
      color: "#BF930B",
      value: summary.mails,
      numeric: true,
      hint: "عدد الرسائل البريدية المسجَّلة.",
    },

    {
      key: "kind",
      label: "النوع",
      group: "البيانات",
      icon: "building",
      color: "#3672EA",
      value:
        ACCOUNT_KIND_MAP[account.kind as "COMPANY" | "INDIVIDUAL"]?.label ??
        "—",
      hint: "شركة أم فرد.",
    },
    {
      key: "industry",
      label: "النشاط",
      group: "البيانات",
      icon: "chartBar",
      color: "#9BA3AE",
      value: account.industry || "—",
      hint: "قطاع عمله.",
    },
    {
      key: "city",
      label: "المدينة",
      group: "البيانات",
      icon: "home",
      color: "#6F7784",
      value: account.city || "—",
      note: account.address || undefined,
      hint: "موقعه — ويهمّ من يوزّع المندوبين جغرافيًّا.",
    },
    {
      key: "fromLeads",
      label: "من عملاء محتملين",
      group: "البيانات",
      icon: "arrowRightLeft",
      color: "#8791A1",
      value: summary.fromLeads,
      numeric: true,
      note: summary.fromLeads === 0 ? "أُضيف مباشرةً" : undefined,
      hint: "كم عميلًا محتملًا تحوّل إلى هذا الحساب.",
    },
  ];
}

type Tab = "deals" | "contracts" | "projects" | "invoices" | "statements";

/*
 * أقسام ملف العميل الكبيرة — ما يُسحب ويُخفى ويُكبَّر.
 *
 * الجداول الداخلية وتبويبات الصفقات ليست أقسامًا: هي محتوى قسمٍ واحد،
 * وسحبُها منفردةً يفتّت المعنى الذي جُمعت لأجله.
 */
const ACCOUNT_SECTIONS: SectionDef[] = [
  { key: "hub", title: "سجل التعامل", column: "main" },
  { key: "tabs", title: "الصفقات والعقود والفواتير", column: "main" },
  { key: "ratings", title: "آراء العميل", column: "main" },
  { key: "feedback", title: "ملاحظات الفريق", column: "main" },
  { key: "attachments", title: "مستندات العميل", column: "main" },
  { key: "contacts", title: "جهات الاتصال", column: "side" },
  { key: "data", title: "بيانات الحساب", column: "side" },
  { key: "credit", title: "حدّ الائتمان", column: "side" },
  { key: "timeline", title: "ما جرى مع هذا العميل", column: "side" },
  { key: "timelog", title: "سجل الوقت", column: "side" },
];

/* الافتراضي: ما كان معروضًا قبل أن يصير اختيار البطاقات متاحًا. */
const ACCOUNT_CARD_DEFAULTS = [
  "owner",
  "openDeals",
  "wonDeals",
  "billed",
  "collected",
  "outstanding",
  "openActivities",
  "touches",
  "lastActivity",
  "nextAction",
  "contacts",
];

export function AccountProfile() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("deals");
  const [editing, setEditing] = useState(false);
  /* أزرار الرأس — كملف العميل المحتمل سواء: اتصالٌ ورسائل وبريد راسية. */
  /* الرقم المطلوب لا رايةَ فتح — فـ«هاتف آخر» يطلب رقمه هو. */
  const [headerCall, setHeaderCall] = useState<string | null>(null);
  const [headerMail, setHeaderMail] = useState(false);
  const [quickMsg, setQuickMsg] = useState<QuickChannel | null>(null);
  /* تسجيل تحصيلٍ على فاتورةٍ من الملف — والدفعة تدخل الحسابات نفسها. */
  const [paying, setPaying] = useState<Record<string, any> | null>(null);
  /* برفيو الفاتورة في مكانه — نافذةٌ لا إحالة. */
  const [previewInvoice, setPreviewInvoice] = useState<string | null>(null);
  /* مشروعٌ من عقد — النافذة تُفتح من تبويب المشاريع أو من صفّ العقد نفسه. */
  const [projectModal, setProjectModal] = useState<null | {
    contractId?: string;
  }>(null);
  /* بروفايل جهة الاتصال من بطاقتها — وفيه «تعديل» يفتح محرّرها. */
  const [contactView, setContactView] = useState<string | null>(null);
  const [contactEdit, setContactEdit] = useState<ContactRow | null>(null);
  const customFields = useCustomFields("ACCOUNT");
  /* اختيار البطاقات مرفوعٌ إلى الرأس — المنتقي بجوار النقاط لا تحتها. */
  const cardChoice = useCardChoice(
    "goyone.stats.account",
    ACCOUNT_CARD_DEFAULTS,
  );
  /* نمط الملف: ترتيب أقسامه وإخفاؤها وتكبير واحدها — تفضيلٌ يبقى. */
  const layout = useProfileLayout("goyone.layout.account", ACCOUNT_SECTIONS);

  const profile = useQuery({
    queryKey: ["account", id],
    queryFn: () => api<Profile>(`/crm/accounts/${id}`),
  });

  /*
   * خلاصتا البيانات — كملف العميل المحتمل سواء: «آخر ملاحظة للعميل»
   * من أنشطته (مفتاح سجلّ التعامل نفسه فالكاش واحد)، و«آخر ملاحظة
   * للفريق» من ملاحظاته على كاش مكوّنها.
   */
  const hubActivities = useQuery({
    queryKey: ["activities", { accountId: id }],
    queryFn: () =>
      api<{ items: HubActivity[] }>(
        `/crm/activities${qs({ accountId: id, pageSize: 100 })}`,
      ),
    enabled: Boolean(id),
  });
  const teamFeedback = useQuery({
    queryKey: ["feedback", "account", id],
    queryFn: () =>
      api<FeedbackView[]>(
        `/feedback${qs({ entity: "account", entityId: id })}`,
      ),
    enabled: Boolean(id),
  });
  const { byId: teamById } = useTeamDirectory();

  /* التصنيف يُبدَّل من الميتر — كملف العميل المحتمل سواء. */
  const changeRating = useMutation({
    mutationFn: (rating: string) =>
      api(`/crm/accounts/${id}`, { method: "PATCH", body: { rating } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["account", id] });
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

  /*
   * إرسالٌ حرّ من رأس الملف — منطق ملف العميل المحتمل نفسه بلا نشاطٍ
   * يُكمَل: واتساب يجد محادثة الرقم أو يبدؤها على القناة المربوطة،
   * وSMS تُسلَّم لتطبيق الجهاز بنصّها ولا تُعلَّم «أُرسلت» زورًا.
   */
  const sendQuick = useMutation({
    mutationFn: async ({
      channel,
      text,
      file,
    }: {
      channel: QuickChannel;
      text: string;
      file: File | null;
    }) => {
      /* لكل قناةٍ رقمُها الموجَّه — التوجيه انتقل مع التحويل كما هو. */
      const acc = profile.data?.account;
      const routed = (which: string | null | undefined): string | null =>
        which === "altPhone"
          ? (acc?.altPhone ?? acc?.phone ?? null)
          : (acc?.phone ?? acc?.altPhone ?? null);

      if (channel === "SMS") {
        const num = routed(acc?.smsPhone);
        if (!num) throw new Error("لا رقم مسجَّل لهذا العميل.");
        window.location.href = `sms:${num}?body=${encodeURIComponent(text)}`;
        return "sms-handoff" as const;
      }

      let convId: string | null = null;
      if (channel === "WHATSAPP") {
        const wa = whatsappNumber(routed(acc?.waPhone));
        if (!wa) throw new Error("يلزم رقم هاتف بمفتاح دولة لإرسال واتساب.");
        const found = await api<{ conversationId: string | null }>(
          `/meta/by-phone${qs({ phone: wa })}`,
        );
        convId = found.conversationId;
        if (!convId) {
          const channels =
            await api<Array<{ id: string; kind: string; isActive: boolean }>>(
              "/meta/channels",
            );
          const ch = channels.find((c) => c.kind === "WHATSAPP" && c.isActive);
          if (!ch)
            throw new Error(
              "لا قناة واتساب مربوطة — اربطها من الإعدادات أولًا.",
            );
          const conv = await api<{ id: string }>("/meta/conversations", {
            method: "POST",
            body: { channelId: ch.id, phone: wa, accountId: id },
          });
          convId = conv.id;
        }
      } else {
        const convs = await api<
          Array<{ id: string; accountId: string | null; channelKind: string }>
        >("/meta/conversations");
        const conv = convs.find(
          (c) => c.channelKind === channel && c.accountId === id,
        );
        if (!conv) {
          const label = channel === "MESSENGER" ? "ماسنجر" : "إنستغرام";
          throw new Error(
            `لا محادثة ${label} مربوطة بهذا العميل بعد — المنصّة لا تسمح ببدء محادثة من طرفنا.`,
          );
        }
        convId = conv.id;
      }

      let view: { status: string; error: string | null };
      if (file) {
        const form = new FormData();
        form.append("file", file);
        if (text) form.append("caption", text);
        view = await upload(`/meta/conversations/${convId}/media`, form);
      } else {
        view = await api(`/meta/conversations/${convId}/messages`, {
          method: "POST",
          body: { body: text },
        });
      }
      if (view.status === "FAILED")
        throw new Error(view.error ?? "فشل الإرسال.");
      return "sent" as const;
    },
    onSuccess: (kind) => {
      setQuickMsg(null);
      toast(
        kind === "sms-handoff"
          ? "فُتح تطبيق الرسائل بنصّك"
          : "أُرسلت وسُجّلت في محادثتها",
      );
      void queryClient.invalidateQueries({ queryKey: ["meta"] });
      void queryClient.invalidateQueries({ queryKey: ["timeline"] });
    },
  });

  if (profile.isError) {
    return (
      <div className="page">
        <ErrorState error={profile.error} />
      </div>
    );
  }

  if (profile.isPending) {
    return (
      <div className="page">
        <div
          className="skeleton"
          style={{ height: 28, width: 260, marginBottom: 20 }}
        />
        <div className="skeleton" style={{ height: 90, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 220 }} />
      </div>
    );
  }

  const {
    account,
    contacts,
    deals,
    quotations,
    contracts = [],
    invoices,
    payments,
    activities,
    summary,
  } = profile.data;

  /* لكل قناةٍ رقمُها الموجَّه — كملف العميل المحتمل سواء. */
  const waSource: string | null =
    account.waPhone === "altPhone"
      ? (account.altPhone ?? account.phone ?? null)
      : (account.phone ?? account.altPhone ?? null);
  const smsSource: string | null =
    account.smsPhone === "altPhone"
      ? (account.altPhone ?? account.phone ?? null)
      : (account.phone ?? account.altPhone ?? null);

  /*
   * الدمج: «بيانات الحساب» و«من ملف العميل المحتمل» بطاقةٌ واحدة.
   *
   * بعد أن صار التحويل ينقل الحقول كلها صار القسمان تكرارًا. الحساب
   * هو المصدر، وما خلا منه (حسابات حُوِّلت قبل التوحيد) يُقرأ من سجلّ
   * أصله المحتمل — الباقي بعد التحويل — ويُعرض في الصفوف نفسها.
   */
  const origin = (profile.data.originLeads ?? [])[0] ?? null;
  const extraOrigins = (profile.data.originLeads ?? []).slice(1);
  const merged = {
    altPhone: (account.altPhone ?? origin?.altPhone ?? null) as string | null,
    messengerHandle: (account.messengerHandle ??
      origin?.messengerHandle ??
      null) as string | null,
    instagramHandle: (account.instagramHandle ??
      origin?.instagramHandle ??
      null) as string | null,
    mapsUrl: (account.mapsUrl ?? origin?.mapsUrl ?? null) as string | null,
    source: (account.source ?? origin?.source ?? null) as string | null,
    city: (account.city ?? origin?.city ?? null) as string | null,
    address: (account.address ?? origin?.address ?? null) as string | null,
    notes: (account.notes ?? origin?.notes ?? null) as string | null,
  };

  return (
    <div className="page">
      {/*
        الترويسة تلتصق — كملف العميل المحتمل وللسبب نفسه: الملف طويل،
        ومن نزل إلى آخره أحوج ما يكون إلى «اتصل» و«عدّل».
      */}
      <div className="record-head sticky-bar">
        <PageHead
          title={account.name}
          avatar={
            <Avatar name={account.name} src={account.avatarUrl} size={44} />
          }
          subtitle={
            [account.industry, account.city].filter(Boolean).join(" — ") ||
            undefined
          }
          actions={
            <>
              {/*
              قنوات التواصل أزرارًا من عائلة الأزرار نفسها — كرأس ملف
              العميل المحتمل سواء: الاتصال بلوحته الراسية، والرسائل
              بالمُرسِل الراسي بثيمة قناته، والبريد من صندوق الشركة.
            */}
              <button
                className="btn btn-sm btn-icon"
                disabled={!account.phone}
                onClick={() => account.phone && setHeaderCall(account.phone)}
                title={
                  account.phone ? `اتصال — ${account.phone}` : "لا رقم مسجَّل"
                }
                aria-label="اتصال"
              >
                <Icon name="phone" size={15} />
              </button>
              <button
                className="btn btn-sm btn-icon"
                disabled={!whatsappNumber(waSource)}
                onClick={() => setQuickMsg("WHATSAPP")}
                title={
                  whatsappNumber(waSource)
                    ? "رسالة واتساب — تُرسَل مباشرة"
                    : "يلزم رقم بمفتاح دولة"
                }
                aria-label="واتساب"
              >
                <Icon name="chat" size={15} />
              </button>
              <button
                className="btn btn-sm btn-icon"
                disabled={!smsSource}
                onClick={() => setQuickMsg("SMS")}
                title={
                  smsSource
                    ? "رسالة SMS — تُكتب هنا وتُرسَل من جهازك"
                    : "لا رقم مسجَّل"
                }
                aria-label="SMS"
              >
                <Icon name="sms" size={15} />
              </button>
              <button
                className="btn btn-sm btn-icon"
                disabled={!account.email}
                onClick={() => setHeaderMail(true)}
                title={
                  account.email
                    ? `راسل ${account.email} من صندوق الشركة`
                    : "لا بريد مسجَّل"
                }
                aria-label="بريد"
              >
                <Icon name="mail" size={15} />
              </button>
              {/* ترتيب رأس ملف العميل المحتمل حرفيًّا — والتقييم في خانة «تحويل». */}
              <TimerButton
                entity="account"
                entityId={id}
                className="btn btn-sm btn-ghost"
              />
              <Link to="/crm/accounts" className="btn btn-sm">
                <Icon name="arrowLeft" size={15} />
                رجوع
              </Link>
              <button className="btn btn-sm" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} />
                تعديل
              </button>
              <RatingLinkButton
                kind="ACCOUNT"
                entityId={id}
                className="btn btn-sm btn-primary"
              />
              <DeleteButton
                entity="account"
                id={id}
                endpoint={`/crm/accounts/${id}`}
                onDeleted={() => navigate("/crm/accounts")}
              />
              {/* تغيير النمط: الأقسام تُسحب وتُخفى وتُكبَّر بأمر صاحب الشاشة. */}
              <ProfileLayoutButton api={layout} />
              {/* «اختر موعدك» — رابطٌ يُرسل فيحجز العميل بنفسه. */}
              <BookingLinkButton
                className="btn btn-sm"
                email={account.email}
                link={{ accountRefId: id }}
              />
              {/* منتقي البطاقات في الرأس بجوار النقاط — لا زرًّا يُبحث عنه تحت. */}
              <CardPickerButton
                choices={accountCards(account, summary)}
                chosen={cardChoice.chosen}
                onChange={cardChoice.setChosen}
                onReset={cardChoice.reset}
                className="btn btn-sm"
              />
              {/* النقاط محدودةٌ كإخوتها — الشبحية كانت تُقرأ زخرفةً لا زرًّا. */}
              <AccountQuickActions
                account={{
                  id,
                  name: account.name,
                  ownerId: account.ownerId ?? null,
                  email: account.email,
                  phone: account.phone,
                }}
                showOpen={false}
                className="btn btn-sm btn-icon"
                onChanged={() => void queryClient.invalidateQueries()}
              />
            </>
          }
        />
      </div>

      {/*
        الملخّص المالي أعلى الشاشة عمدًا.
        السؤال الأول عند فتح ملف عميل هو: كم علينا وكم لنا؟

        والبطاقات تُختار كما تُختار في القوائم: المحاسب يريد المستحقّ
        والمحصَّل، ومن يتابع الحساب يريد آخر نشاط والنشاط القادم — والشاشة
        واحدة.
      */}
      <RecordStats
        items={accountCards(account, summary)}
        storageKey="goyone.stats.account"
        defaults={ACCOUNT_CARD_DEFAULTS}
        /* المنتقي في رأس الصفحة — فلا يتكرّر هنا. */
        external={cardChoice}
      />

      {editing && (
        <AccountEditor account={account} onClose={() => setEditing(false)} />
      )}

      <ProfileLayoutProvider api={layout}>
        <ProfileLayoutBar api={layout} />
        <div className="two-col">
          <LayoutColumn api={layout} column="main">
            {/*
            سجل التعامل بطاقةً مستقلة في صدر العمود — كملف العميل
            المحتمل سواء تصميمًا وترتيبًا، والمكوّن هو نفسه.
          */}
            <Section layoutKey="hub">
              <ActivityHub
                entity="account"
                record={{
                  id,
                  name: account.name,
                  phone: account.phone ?? null,
                  altPhone: account.altPhone ?? null,
                  email: account.email ?? null,
                  waPhone: account.waPhone ?? null,
                  smsPhone: account.smsPhone ?? null,
                  mapsUrl: account.mapsUrl ?? null,
                }}
                /* ميتر التصنيف بألوانه — كسجل تعامل العميل المحتمل سواء. */
                tabsExtra={
                  <RatingMeter
                    rating={account.rating ?? "WARM"}
                    pending={changeRating.isPending}
                    onChange={(r) => changeRating.mutate(r)}
                  />
                }
              />
            </Section>

            <Section layoutKey="tabs">
              <div className="card">
                {/*
              الترتيب الذي يقرأ به المتابع علاقةَ العميل: الصفقات (لوحة
              عروض الأسعار) فالعقود فالمشاريع فالفواتير فكشوف الحساب —
              ثم عُدّة الوكالة وجهات الاتصال.
            */}
                {/* أقراصٌ بأيقوناتها وعدّاداتها — النشط يبيَضّ ويحمل ظلًّا فيُرى. */}
                <div className="acct-tabs" role="tablist">
                  {(
                    [
                      ["deals", "الصفقات", "briefcase", quotations.length],
                      ["contracts", "العقود", "clipboard", contracts.length],
                      ["projects", "المشاريع", "rocket", null],
                      ["invoices", "الفواتير", "receipt", invoices.length],
                      ["statements", "كشوف الحساب", "bank", null],
                    ] as Array<
                      [
                        Tab,
                        string,
                        Parameters<typeof Icon>[0]["name"],
                        number | null,
                      ]
                    >
                  ).map(([key, label, icon, count]) => (
                    <button
                      key={key}
                      className="acct-tab"
                      role="tab"
                      aria-selected={tab === key}
                      onClick={() => setTab(key)}
                    >
                      <Icon name={icon} size={14} />
                      {label}
                      {count !== null && (
                        <span className="acct-tab-n num">{count}</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* المفتاح هو التبويب: تغيّره يُعيد الحركة فيُعرف أن ما تحته تبدّل. */}
                <div className="card-body tab-panel" key={tab}>
                  {tab === "deals" && (
                    <QuotationsBoard
                      accountId={id}
                      quotations={quotations}
                      onInvoiceCreated={setPreviewInvoice}
                    />
                  )}

                  {tab === "contracts" && (
                    <ContractsTab
                      accountId={id}
                      contracts={contracts}
                      onCreateProject={(contractId) =>
                        setProjectModal({ contractId })
                      }
                    />
                  )}

                  {tab === "projects" && (
                    <AccountProjects
                      accountId={id}
                      onAdd={() => setProjectModal({})}
                    />
                  )}

                  {tab === "invoices" && (
                    <InvoicesTab
                      accountId={id}
                      quotations={quotations}
                      invoices={invoices}
                      payments={payments}
                      onPreview={setPreviewInvoice}
                      onPay={setPaying}
                    />
                  )}

                  {tab === "statements" && <AccountStatements accountId={id} />}
                </div>
              </div>
            </Section>

            <Section layoutKey="ratings">
              <AccountRatings accountId={id} />
            </Section>

            <Section layoutKey="feedback">
              <Feedback entity="account" entityId={id} title="ملاحظات الفريق" />
            </Section>

            <Section layoutKey="attachments">
              <Attachments
                entity="account"
                entityId={id}
                title="مستندات العميل"
              />
            </Section>
          </LayoutColumn>

          <LayoutColumn api={layout} column="side">
            {/*
            جهات الاتصال في صدر العمود لا تبويبًا مدفونًا: من يفتح ملف
            شركةٍ يريد «من أكلّم؟» قبل كل شيء — الاسم والرقم والبريد
            بروابطها، والرئيسية معلَّمة.
          */}
            <Section layoutKey="contacts">
              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">جهات الاتصال</h2>
                  {contacts.length > 0 && (
                    <span className="tiny mute">{contacts.length}</span>
                  )}
                </div>
                <div className="card-body">
                  {contacts.length === 0 ? (
                    <p className="mute small">لا جهات اتصال بعد.</p>
                  ) : (
                    contacts.map((c) => (
                      /*
                    الأيقونات بدل الأرقام: الاتصال بلوحته الراسية وواتساب
                    وSMS بمُرسِلهما والبريد من صندوق الشركة — مكوّن قنوات
                    العملاء والمحتملين نفسه، والرقم في تلميح كل أيقونة.
                  */
                      <div key={c.id} className="ld-contact">
                        <div
                          className="row"
                          style={{ gap: 8, alignItems: "center" }}
                        >
                          <Avatar name={c.name} size={28} />
                          {/* الاسم وتحته مسمّاه — من هو وما صفته في نظرة. */}
                          <span className="col" style={{ gap: 1, minWidth: 0 }}>
                            <span
                              className="row"
                              style={{ gap: 6, alignItems: "center" }}
                            >
                              {/* الاسم يفتح بروفايل الجهة — وفيه «تعديل» للتحديث. */}
                              <button
                                type="button"
                                className="link-btn"
                                style={{ fontWeight: 600, padding: 0 }}
                                onClick={() => setContactView(c.id)}
                                title="افتح بروفايل جهة الاتصال"
                              >
                                {c.name}
                              </button>
                              {c.isPrimary && (
                                <Badge kind="brand">رئيسية</Badge>
                              )}
                            </span>
                            <span className="tiny mute">
                              {c.jobTitle || "بلا مسمًّى وظيفي"}
                            </span>
                          </span>
                          <span
                            style={{ marginInlineStart: "auto", flex: "none" }}
                          >
                            <ReachIcons
                              name={c.name}
                              phone={c.phone}
                              email={c.email}
                              link={{ accountRefId: id, contactId: c.id }}
                              size={14}
                              waPhone={
                                c.waPhone === "altPhone" ? c.altPhone : c.phone
                              }
                              smsPhone={
                                c.smsPhone === "altPhone" ? c.altPhone : c.phone
                              }
                            />
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Section>

            <Section layoutKey="data">
              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">بيانات الحساب</h2>
                </div>
                <div className="card-body">
                  {/*
                النوع شارةً — ومعه تصنيفُ أصله المحتمل وقيمتُه التقديرية
                قراءةً: تاريخ ما قبل التحويل في البطاقة نفسها لا في قسمٍ ثانٍ.
              */}
                  <div className="ld-badges">
                    <Badge
                      kind={
                        ACCOUNT_KIND_MAP[
                          account.kind as "COMPANY" | "INDIVIDUAL"
                        ]?.kind
                      }
                    >
                      {
                        ACCOUNT_KIND_MAP[
                          account.kind as "COMPANY" | "INDIVIDUAL"
                        ]?.label
                      }
                    </Badge>
                    {account.isActive === false && (
                      <Badge kind="danger">موقوف</Badge>
                    )}
                    <span className="ld-rate" aria-label="الأولوية">
                      {[...LEAD_RATING_DEFS].reverse().map((r) => (
                        <span
                          key={r.key}
                          className={`ld-rate-btn${
                            (account.rating ?? "WARM") === r.key
                              ? ` on rate-${r.key.toLowerCase()}`
                              : ""
                          }`}
                        >
                          {r.label}
                        </span>
                      ))}
                    </span>
                    {origin?.value ? (
                      <span className="ld-worth num">
                        {money(origin.value)}
                      </span>
                    ) : null}
                  </div>

                  {/*
                التواصل صفوفًا بأيقوناتها: الرقم يُتّصل به عبر الكونسول،
                والبريد يُراسَل، والموقع يُفتح — لا نصوصٌ تُنسخ باليد.
              */}
                  <div className="ld-rows">
                    <div className="ld-row">
                      <span className="ld-ico">
                        <Icon name="phone" size={14} />
                      </span>
                      <span className="ld-label">الهاتف</span>
                      <span className="ld-value num">
                        {account.phone ? (
                          /* الاتصال في مكانه — لوحةٌ راسيةٌ كسائر أزرار القنوات. */
                          <button
                            type="button"
                            className="link-btn num"
                            dir="ltr"
                            onClick={() =>
                              account.phone && setHeaderCall(account.phone)
                            }
                            title={`اتصل — ${account.phone}`}
                          >
                            {account.phone}
                          </button>
                        ) : (
                          <span className="mute">—</span>
                        )}
                      </span>
                      {account.phone &&
                        (account.waPhone === "phone" ||
                          account.smsPhone === "phone") && (
                          <span className="ld-tags">
                            {account.waPhone === "phone" && (
                              <span className="ld-tag is-wa">واتساب</span>
                            )}
                            {account.smsPhone === "phone" && (
                              <span className="ld-tag">SMS</span>
                            )}
                          </span>
                        )}
                    </div>

                    {merged.altPhone && (
                      <div className="ld-row">
                        <span className="ld-ico">
                          <Icon name="phone" size={14} />
                        </span>
                        <span className="ld-label">هاتف آخر</span>
                        <span className="ld-value num">
                          <button
                            type="button"
                            className="link-btn num"
                            dir="ltr"
                            onClick={() =>
                              merged.altPhone && setHeaderCall(merged.altPhone)
                            }
                            title={`اتصل — ${merged.altPhone}`}
                          >
                            {merged.altPhone}
                          </button>
                        </span>
                        {(account.waPhone === "altPhone" ||
                          account.smsPhone === "altPhone") && (
                          <span className="ld-tags">
                            {account.waPhone === "altPhone" && (
                              <span className="ld-tag is-wa">واتساب</span>
                            )}
                            {account.smsPhone === "altPhone" && (
                              <span className="ld-tag">SMS</span>
                            )}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="ld-row">
                      <span className="ld-ico">
                        <Icon name="mail" size={14} />
                      </span>
                      <span className="ld-label">البريد</span>
                      <span className="ld-value num">
                        {account.email ? (
                          <a
                            className="link"
                            dir="ltr"
                            href={`mailto:${account.email}`}
                          >
                            {account.email}
                          </a>
                        ) : (
                          <span className="mute">—</span>
                        )}
                      </span>
                    </div>

                    {merged.messengerHandle &&
                      (() => {
                        const s = originSocial(
                          merged.messengerHandle,
                          "messenger",
                        );
                        return (
                          <div className="ld-row">
                            <span className="ld-ico">
                              <ChannelLogo kind="MESSENGER" size={15} />
                            </span>
                            <span className="ld-label">ماسنجر</span>
                            <span className="ld-value num">
                              <a
                                className="link"
                                dir="ltr"
                                href={s.href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {s.handle}
                              </a>
                            </span>
                          </div>
                        );
                      })()}

                    {merged.instagramHandle &&
                      (() => {
                        const s = originSocial(
                          merged.instagramHandle,
                          "instagram",
                        );
                        return (
                          <div className="ld-row">
                            <span className="ld-ico">
                              <ChannelLogo kind="INSTAGRAM" size={15} />
                            </span>
                            <span className="ld-label">إنستغرام</span>
                            <span className="ld-value num">
                              <a
                                className="link"
                                dir="ltr"
                                href={s.href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {s.handle}
                              </a>
                            </span>
                          </div>
                        );
                      })()}

                    {merged.mapsUrl && (
                      <div className="ld-row">
                        <span className="ld-ico">
                          <Icon name="building" size={14} />
                        </span>
                        <span className="ld-label">موقع العميل</span>
                        <span className="ld-value">
                          <a
                            className="link"
                            href={merged.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            افتح على الخريطة
                          </a>
                        </span>
                      </div>
                    )}

                    {account.website && (
                      <div className="ld-row">
                        <span className="ld-ico">
                          <Icon name="link" size={14} />
                        </span>
                        <span className="ld-label">الموقع</span>
                        <span className="ld-value num">
                          <a
                            className="link"
                            dir="ltr"
                            href={
                              /^https?:\/\//i.test(account.website)
                                ? account.website
                                : `https://${account.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {account.website}
                          </a>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ترتيب قائمة بيانات العميل المحتمل نفسه — ثم خصوصيات الحساب. */}
                  <dl className="detail-list mt-4">
                    <dt>المصدر</dt>
                    <dd>{merged.source ?? "—"}</dd>

                    <dt>المدينة</dt>
                    <dd>{merged.city ?? "—"}</dd>

                    <dt>آخر تواصل</dt>
                    <dd>
                      {summary.lastActivityAt
                        ? dateTime(summary.lastActivityAt)
                        : "لم يتم"}
                    </dd>

                    <dt>أُضيف في</dt>
                    <dd>{date(account.createdAt)}</dd>

                    {/* أثر التحويل داخل البطاقة نفسها — لا قسمَ ثانيًا له. */}
                    {origin?.convertedAt && (
                      <>
                        <dt>حُوِّل في</dt>
                        <dd>{date(origin.convertedAt)}</dd>
                      </>
                    )}
                    {origin && (
                      <>
                        <dt>ملفه الأصلي</dt>
                        <dd>
                          <Link to={`/crm/leads/${origin.id}`} className="link">
                            ملف العميل المحتمل — {origin.name}
                          </Link>
                          {extraOrigins.map((o) => (
                            <Fragment key={o.id}>
                              {" · "}
                              <Link to={`/crm/leads/${o.id}`} className="link">
                                {o.name}
                              </Link>
                            </Fragment>
                          ))}
                        </dd>
                      </>
                    )}

                    <dt>الاسم القانوني</dt>
                    <dd>{account.legalName ?? "—"}</dd>

                    <dt>الرقم الضريبي</dt>
                    <dd className="num">{account.taxNumber ?? "—"}</dd>

                    <dt>النشاط</dt>
                    <dd>{account.industry ?? "—"}</dd>

                    <dt>العنوان</dt>
                    <dd>{merged.address ?? "—"}</dd>

                    {/*
                  الحقول المخصَّصة داخل القائمة نفسها لا في بطاقة منفصلة:
                  هي بيانات العميل كغيرها، وفصلها يوحي بأنها شيء آخر.
                */}
                    {activeFields(customFields.data).map((def) => (
                      <Fragment key={def.key}>
                        <dt>{def.label}</dt>
                        <dd
                          className={
                            def.type === "NUMBER" || def.type === "DATE"
                              ? "num"
                              : undefined
                          }
                        >
                          {formatValue(
                            def.type,
                            (account.customFields ?? {})[def.key],
                          )}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>

                  {/* ملاحظات الملف الثابتة — كموضعها في بيانات العميل المحتمل. */}
                  {merged.notes && (
                    <div className="mt-4">
                      <div className="label mb-4">ملاحظات</div>
                      <p className="small" style={{ whiteSpace: "pre-wrap" }}>
                        {merged.notes}
                      </p>
                    </div>
                  )}

                  {/*
                خلاصة الصفحة — كملف العميل المحتمل سواء: آخر ما قيل عن
                العميل وآخر ما قاله الفريق، يُقرآن بنظرةٍ قبل السجلات.
              */}
                  {(() => {
                    const lastNote = (hubActivities.data?.items ?? [])
                      .filter((a) => a.type === "NOTE")
                      .sort((a, b) =>
                        (a.createdAt ?? "") < (b.createdAt ?? "") ? 1 : -1,
                      )[0];
                    if (!lastNote) return null;
                    return (
                      <div className="mt-4">
                        <div className="label mb-4">آخر ملاحظة للعميل</div>
                        <div className="ld-last">
                          <span className="small" style={{ fontWeight: 600 }}>
                            {lastNote.subject}
                          </span>
                          {lastNote.body && (
                            <span className="small">{lastNote.body}</span>
                          )}
                          <span className="tiny mute">
                            {dateTime(lastNote.createdAt)}
                            {lastNote.noteAbout &&
                              ACTIVITY_TYPE_MAP[
                                lastNote.noteAbout as HubActivity["type"]
                              ] &&
                              ` — تخصّ ${ACTIVITY_TYPE_MAP[lastNote.noteAbout as HubActivity["type"]]!.plural}`}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {(() => {
                    const flat = (teamFeedback.data ?? []).flatMap((r) => [
                      r,
                      ...r.replies,
                    ]);
                    const lastFb = [...flat].sort((a, b) =>
                      a.createdAt < b.createdAt ? 1 : -1,
                    )[0];
                    if (!lastFb) return null;
                    return (
                      <div className="mt-4">
                        <div className="label mb-4">آخر ملاحظة للفريق</div>
                        <div className="ld-last">
                          <span className="small">
                            {lastFb.body
                              ? renderMentions(
                                  lastFb.body,
                                  (uid) => teamById.get(uid)?.name,
                                )
                              : "تسجيل صوتي — استمع إليه في ملاحظات الفريق"}
                          </span>
                          <span className="tiny mute">
                            {lastFb.authorName ?? "مستخدم سابق"} ·{" "}
                            {dateTime(lastFb.createdAt)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Section>

            <Section layoutKey="credit">
              <CreditPanel
                accountId={id}
                currentLimit={account.creditLimit ?? null}
              />
            </Section>

            {/*
            كملفّ العميل المحتمل سواء: «ما جرى» تحت البيانات لا في عمودٍ
            مقابل، فمن قرأ ملخّص العميل ينزل بعينه إلى تاريخه بلا قفزة.
          */}
            <Section layoutKey="timeline">
              {/* مختصرًا كملف العميل المحتمل: آخر خمسة والتكبير للسجل كله. */}
              <RecordTimeline
                entity="account"
                id={id}
                title="ما جرى مع هذا العميل"
                compact
              />
            </Section>

            <Section layoutKey="timelog">
              <TimeLog entity="account" entityId={id} />
            </Section>
          </LayoutColumn>
        </div>
      </ProfileLayoutProvider>

      {/* اتصال الرأس — لوحة الكونسول راسيةً في الملف: تبدأ وتُنهى هنا. */}
      {headerCall && (
        <QuickCallModal
          clientName={account.name}
          phone={headerCall}
          link={{ accountId: id }}
          onClose={() => {
            setHeaderCall(null);
            void queryClient.invalidateQueries({ queryKey: ["activities"] });
            void queryClient.invalidateQueries({ queryKey: ["timeline"] });
          }}
        />
      )}

      {/* بريد الرأس — رسالة حرّة من صندوق الشركة بلا نشاطٍ تُكمله. */}
      {headerMail && account.email && (
        <MailCompose
          to={account.email}
          link={{ accountRefId: id }}
          onClose={() => setHeaderMail(false)}
          onSent={() => {
            setHeaderMail(false);
            void queryClient.invalidateQueries({ queryKey: ["timeline"] });
          }}
        />
      )}

      {/* برفيو الفاتورة = مستندُها المطبوع نفسه — ما يراه العميل على الورق. */}
      {/* بروفايل جهة الاتصال — و«تعديل» يسلّم لمحرّرها فالتحديث من هنا. */}
      {contactView && (
        <ContactDetailModal
          id={contactView}
          onClose={() => setContactView(null)}
          onEdit={(c) => {
            setContactView(null);
            setContactEdit(c);
          }}
        />
      )}
      {contactEdit && (
        <ContactModal
          contact={contactEdit}
          onClose={() => setContactEdit(null)}
          onSaved={() => {
            setContactEdit(null);
            void queryClient.invalidateQueries({ queryKey: ["account", id] });
            void queryClient.invalidateQueries({ queryKey: ["contact"] });
          }}
        />
      )}

      {previewInvoice && (
        <DocPreview
          path={`/print/invoice/${previewInvoice}`}
          title="معاينة الفاتورة"
          onClose={() => setPreviewInvoice(null)}
        />
      )}

      {/* مشروعٌ يقوم على عقد — عقدٌ سارٍ يُختار، والعقد الواحد مشروعٌ واحد. */}
      {projectModal && (
        <ProjectFromContractModal
          accountId={id}
          contracts={contracts}
          presetContractId={projectModal.contractId}
          onClose={() => setProjectModal(null)}
        />
      )}

      {/* تحصيلٌ يُسجَّل من الملف — ويدخل الحسابات نفسها فورًا. */}
      {paying && (
        <RecordPaymentModal
          accountId={id}
          invoice={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            void queryClient.invalidateQueries({ queryKey: ["account", id] });
            void queryClient.invalidateQueries({ queryKey: ["credit"] });
          }}
        />
      )}

      {/* واتساب وSMS — مُرسِلٌ راسٍ بلون قناته، كرأس ملف العميل المحتمل. */}
      {quickMsg && (
        <QuickMessageModal
          channel={quickMsg}
          clientName={account.name}
          hint={
            quickMsg === "WHATSAPP"
              ? "تُرسَل عبر قناة واتساب المربوطة وتُسجَّل في محادثة العميل — والوسائط كذلك."
              : quickMsg === "SMS"
                ? "تُفتح في تطبيق الرسائل على جهازك بنصّها — لا بوابة SMS مربوطة بعد."
                : "تُرسَل في محادثة العميل القائمة."
          }
          pending={sendQuick.isPending}
          error={
            sendQuick.error instanceof Error ? sendQuick.error.message : null
          }
          onSend={(text, file) =>
            sendQuick.mutate({ channel: quickMsg, text, file })
          }
          onClose={() => {
            sendQuick.reset();
            setQuickMsg(null);
          }}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   بيانات العميل المحتمل الأصلي — كتلة عرضٍ صِرفة
   ========================================================================== */

/** «@اسم» من معرّفٍ أو رابطٍ كامل — كدالّة ملف العميل المحتمل سواء. */
function originSocial(raw: string, base: "instagram" | "messenger") {
  const t = raw.trim();
  const m = t.match(/^https?:\/\/[^/]+\/([^/?#]+)/i);
  const handle = (m ? m[1]! : t).replace(/^@/, "");
  const href = /^https?:\/\//i.test(t)
    ? t
    : base === "instagram"
      ? `https://instagram.com/${encodeURIComponent(handle)}`
      : `https://m.me/${encodeURIComponent(handle)}`;
  return { handle: `@${handle}`, href };
}

/* ==========================================================================
   لوحة الصفقات — أعمدة عروض الأسعار، والمتابعة من هنا
   ========================================================================== */

/**
 * الصفقة هنا هي عرض السعر بمراحله: يُنشأ فيقف في «جديدة»، ويُرسَل
 * فينتقل، ويُقبل ففائزة أو يُرفض/تنقضي صلاحيته فخاسرة. البطاقة ملخّصٌ
 * والنقر يفتح العرض نفسه في المبيعات — فالعمل هناك والمتابعة هنا.
 */
/* أسماءٌ من كلمة: الرأس سطرٌ واحد يتّسع للاسم والعدّاد والمجموع معًا. */
const QUOTE_COLUMNS: Array<{
  key: string;
  label: string;
  match: string[];
  tone: string;
}> = [
  { key: "NEW", label: "جديدة", match: ["DRAFT"], tone: "var(--brand)" },
  /*
   * وما يُتابَع يُقرأ هنا مع ما أُرسل.
   *
   * «قيد المتابعة» حالةٌ قائمة في المبيعات، وهذه اللوحةُ أربعةُ أعمدةٍ
   * عمدًا — فلو لم تُذكر لسقط العرضُ من الأعمدة كلِّها واختفى من ملفّ
   * صاحبه وهو قائم. فيُضمّ إلى أقربها معنًى: كلاهما صادرٌ ينتظر قرارًا.
   */
  {
    key: "SENT",
    label: "أُرسلت",
    match: ["SENT", "NEGOTIATION"],
    tone: "#BF930B",
  },
  { key: "WON", label: "فائزة", match: ["ACCEPTED"], tone: "var(--success)" },
  {
    key: "LOST",
    label: "خاسرة",
    match: ["REJECTED", "EXPIRED"],
    tone: "var(--danger)",
  },
];

/** شريط أدوات التبويب: بحثٌ ينمو وأزرار الفعل في طرفه — واحدٌ لكل الأقسام. */
function TabTools({
  value,
  onChange,
  placeholder,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="tab-tools">
      {/* صندوق البحث الموحّد — الشكل نفسه في كل النظام لا نسخةٌ لكل شاشة. */}
      <SearchBox value={value} onChange={onChange} placeholder={placeholder} />
      {children && (
        <div
          className="row"
          style={{ gap: 8, marginInlineStart: "auto", flexWrap: "wrap" }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** مطابقة بحثٍ بسيطة: كل الحقول المعروضة نصًّا واحدًا يُفتَّش فيه. */
function matches(
  needle: string,
  ...fields: Array<string | number | null | undefined>
) {
  const n = needle.trim().toLowerCase();
  if (!n) return true;
  return fields.some((f) => f != null && String(f).toLowerCase().includes(n));
}

/**
 * لوحة الصفقات — لعميلٍ أو لمحتمل، بالشكل نفسه.
 *
 * المحتملُ تُفتح عليه عروضُه قبل تحويله، فيرى ملفُه اللوحةَ ذاتها:
 * أعمدةٌ بحالاتها وبطاقاتٌ بخطوتها التالية. وما لا يصلح للمحتمل — العقدُ
 * والفاتورة — لا يُعرض له: قبولُ عرضه يستدعي تحويلَه أولًا (`onQualified`).
 */
export function QuotationsBoard({
  accountId,
  leadId = null,
  quotations,
  onInvoiceCreated,
  onQualified,
}: {
  accountId: string | null;
  leadId?: string | null;
  quotations: Array<Record<string, any>>;
  onInvoiceCreated: (invoiceId: string) => void;
  /** قُبل عرضُ محتملٍ — فليُحوَّل. */
  onQualified?: (leadId: string, quotationId: string) => void;
}) {
  const from = leadId && !accountId ? "lead" : "account";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState("");

  const refresh = () => {
    if (accountId)
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
    void queryClient.invalidateQueries({ queryKey: ["lead-deals"] });
    void queryClient.invalidateQueries({ queryKey: ["quotations"] });
    void queryClient.invalidateQueries({ queryKey: ["contracts"] });
  };

  /*
   * سلسلة الصفقة على بطاقتها: تُعلَّم مُرسَلة، فتُسجَّل الموافقة،
   * فيُنشأ العقد، فتصدر الفاتورة — كل خطوةٍ زرٌّ يظهر في أوانه فقط،
   * والخادم يحكم (خصمٌ فوق الحدّ، فاتورةٌ بلا عقد…) ورسالتُه تُعرض كما هي.
   */
  const act = useMutation({
    mutationFn: async ({
      q,
      step,
    }: {
      q: Record<string, any>;
      step: ChainStep;
    }) => {
      if (step === "send")
        return api<{ id: string }>(`/sales/quotations/${q.id}/send`, {
          method: "POST",
        });
      if (step === "approve")
        return api<{
          id: string;
          needsConversion?: boolean;
          leadId?: string | null;
        }>(`/sales/quotations/${q.id}/decide`, {
          method: "POST",
          body: { decision: "ACCEPTED" },
        });
      if (step === "contract")
        return api<{ id: string }>("/sales/contracts/from-quotation", {
          method: "POST",
          body: {
            quotationId: q.id,
            includeLines: true,
            includeDefaults: true,
          },
        });
      return issueInvoiceFromQuotation(q.id);
    },
    onSuccess: (created, { step, q }) => {
      /* موافقةٌ على عرضِ محتمل: لم تُسجَّل بعد — التحويلُ أولًا، ثم تُسجَّل تلقائيًّا. */
      const gate = created as {
        needsConversion?: boolean;
        leadId?: string | null;
      };
      if (step === "approve" && gate.needsConversion && gate.leadId) {
        toast("حوّل العميل المحتمل إلى عميل لتُسجَّل الموافقة");
        onQualified?.(gate.leadId, q.id);
        return;
      }
      toast(
        step === "send"
          ? "عُلّم العرض مُرسَلًا"
          : step === "approve"
            ? "سُجّلت موافقة العميل — أنشئ العقد حين تتفقان"
            : step === "contract"
              ? "أُنشئ العقد من العرض — راجعه ووقّعه من تبويب العقود"
              : "صدرت الفاتورة عن العقد — هذه معاينتها",
      );
      refresh();
      if (step === "invoice") onInvoiceCreated(created.id);
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "تعذّر إتمام الخطوة."),
  });

  const shown = quotations.filter((q) =>
    matches(search, q.title, q.number, q.total, date(q.issuedAt)),
  );

  return (
    <div>
      <TabTools
        value={search}
        onChange={setSearch}
        placeholder="ابحث بعنوان الصفقة أو رقمها أو قيمتها"
      >
        {/* الإضافة تفتح نموذج عرض السعر والعميلُ مُنتقى — لا صفحةَ قوائم بينهما. */}
        <button
          className="btn btn-sm btn-primary"
          onClick={() =>
            navigate(
              from === "lead"
                ? `/sales/quotations/new?leadId=${leadId}&from=lead`
                : `/sales/quotations/new?accountId=${accountId}&from=account`,
            )
          }
        >
          <Icon name="plus" size={14} />
          إضافة صفقة
        </button>
      </TabTools>

      <div className="qb-grid">
        {QUOTE_COLUMNS.map((col) => {
          const items = shown.filter((q) => col.match.includes(q.status));
          /* قيمة العمود بجوار اسمه — كم يقف في كل مرحلة يُقرأ بنظرة. */
          const sum = items.reduce((n, q) => n + Number(q.total ?? 0), 0);
          return (
            <div
              key={col.key}
              className="qb-col"
              style={{ ["--qb-tone" as string]: col.tone }}
            >
              <div className="qb-col-head">
                {col.label}
                <span className="qb-count num">{items.length}</span>
                <span
                  className="num tiny"
                  style={{ marginInlineStart: "auto" }}
                >
                  {money(sum)}
                </span>
              </div>
              {items.length === 0 && (
                <p className="tiny mute center">
                  {search ? "لا نتيجة هنا." : "لا شيء هنا."}
                </p>
              )}
              {/* أربعُ بطاقاتٍ ظاهرة — وما زاد تمريرٌ داخل العمود نفسه. */}
              <div className="qb-cards">
                {items.map((q, i) => {
                  const contract = pickContract(q.contracts);
                  const invoice = q.invoices?.[0] ?? null;
                  /* الفاتورة تنتظر عقدًا موقَّعًا ساريًا — كما يحكم الخادم سواء. */
                  const contractReady = Boolean(
                    contract &&
                    contract.signedAt &&
                    contract.status === "ACTIVE",
                  );
                  const step: ChainStep | null =
                    q.status === "DRAFT"
                      ? "send"
                      : q.status === "SENT" || q.status === "NEGOTIATION"
                        ? "approve"
                        : q.status === "ACCEPTED" &&
                            !contract &&
                            from !== "lead"
                          ? "contract"
                          : q.status === "ACCEPTED" && contractReady && !invoice
                            ? "invoice"
                            : null;
                  return (
                    /*
                     * البطاقة تنقل إلى العرض وعليها أزرار — فهي div قابلة
                     * للنقر لا button: زرٌّ داخل زرّ ليس HTML صالحًا.
                     */
                    <div
                      key={q.id}
                      role="button"
                      tabIndex={0}
                      className="qb-card"
                      style={{ ["--qb-i" as string]: Math.min(i, 6) }}
                      onClick={() =>
                        navigate(`/sales/quotations/${q.id}?from=${from}`)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          navigate(`/sales/quotations/${q.id}?from=${from}`);
                      }}
                      title="افتح عرض السعر في المبيعات"
                    >
                      {/* العنوان أولًا إن وُجد — فالرقم هويةُ محاسبٍ لا قارئ. */}
                      {q.title && <span className="qb-title">{q.title}</span>}
                      <span
                        className={`num${q.title ? " tiny mute" : " qb-title"}`}
                      >
                        {q.number}
                      </span>
                      <span className="num qb-amount">{money(q.total)}</span>
                      <span className="tiny mute">{date(q.issuedAt)}</span>

                      {/* ما تمّ من السلسلة يُلبَس وسمًا — والنقر يفتح صاحبه. */}
                      {(contract || invoice) && (
                        <span
                          className="row"
                          style={{ gap: 4, flexWrap: "wrap" }}
                        >
                          {contract && (
                            <span
                              role="link"
                              tabIndex={0}
                              className="qb-chip is-ok"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/sales/contracts/${contract.id}?from=account`,
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.stopPropagation();
                                  navigate(
                                    `/sales/contracts/${contract.id}?from=account`,
                                  );
                                }
                              }}
                              title={
                                contractReady
                                  ? "افتح العقد"
                                  : "افتح العقد لتوقيعه وتفعيله — الفاتورة تصدر بعدهما"
                              }
                            >
                              <Icon name="clipboard" size={11} />
                              عقد {contract.number}
                            </span>
                          )}
                          {invoice && (
                            <span
                              role="link"
                              tabIndex={0}
                              className="qb-chip is-ok"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/finance/invoices/${invoice.id}?from=account`,
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.stopPropagation();
                                  navigate(
                                    `/finance/invoices/${invoice.id}?from=account`,
                                  );
                                }
                              }}
                              title="افتح الفاتورة"
                            >
                              <Icon name="receipt" size={11} />
                              فاتورة {invoice.number}
                            </span>
                          )}
                        </span>
                      )}

                      {/* الخطوة التالية وحدها — لا قائمةُ أزرارٍ تُحفَظ. */}
                      {step && (
                        <span className="qb-act">
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={act.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              act.mutate({ q, step });
                            }}
                          >
                            <Icon name={CHAIN_STEPS[step].icon} size={12} />
                            {CHAIN_STEPS[step].label}
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * عقدُ العرض الذي تُبنى عليه الخطوة التالية.
 *
 * إن تعدّدت العقود (ما قبل منع التكرار) فالموقَّعُ الساري هو المعتبر، ثم
 * الساري، ثم أوّلُها — لا يُترك عقدٌ مسودّةٌ يحجب زرَّ الفاتورة عن عقدٍ فُعّل.
 */
function pickContract(
  contracts:
    | Array<{
        id: string;
        number: string;
        status: string;
        signedAt: string | null;
      }>
    | undefined,
): {
  id: string;
  number: string;
  status: string;
  signedAt: string | null;
} | null {
  if (!contracts?.length) return null;
  return (
    contracts.find((c) => c.status === "ACTIVE" && c.signedAt) ??
    contracts.find((c) => c.status === "ACTIVE") ??
    contracts[0] ??
    null
  );
}

/** خطوات السلسلة على البطاقة — زرُّ كلٍّ منها يظهر في أوانه فقط. */
type ChainStep = "send" | "approve" | "contract" | "invoice";
const CHAIN_STEPS: Record<
  ChainStep,
  { label: string; icon: Parameters<typeof Icon>[0]["name"] }
> = {
  send: { label: "تعليمه مُرسَلًا", icon: "send" },
  approve: { label: "موافقة العميل", icon: "check" },
  contract: { label: "إنشاء عقد للعرض", icon: "clipboard" },
  invoice: { label: "إنشاء فاتورة", icon: "receipt" },
};

/* ==========================================================================
   مشاريع العميل — بطاقةٌ لكل مشروع، والنقر يدخل إدارة المشاريع
   ========================================================================== */

function AccountProjects({
  accountId,
  onAdd,
}: {
  accountId: string;
  onAdd: () => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const projects = useQuery({
    queryKey: ["agency", "projects"],
    queryFn: () => api<Array<Record<string, any>>>("/agency/projects"),
  });

  const mine = (projects.data ?? []).filter(
    (p) =>
      p.accountId === accountId && matches(search, p.name, p.contract?.number),
  );

  if (projects.isPending)
    return <div className="skeleton" style={{ height: 90 }} />;

  return (
    <div>
      <TabTools
        value={search}
        onChange={setSearch}
        placeholder="ابحث باسم المشروع أو رقم عقده"
      >
        {/* المشروع يقوم على عقدٍ سارٍ — النافذة تسأل: أيُّ عقدٍ نعمله مشروعًا؟ */}
        <button className="btn btn-sm btn-primary" onClick={onAdd}>
          <Icon name="plus" size={14} />
          إضافة مشروع
        </button>
      </TabTools>

      {mine.length === 0 ? (
        <p className="mute small">
          {search
            ? "لا مشروع يطابق البحث."
            : "لا مشاريع لهذا العميل بعد — أضِف مشروعًا على عقدٍ سارٍ (مشروعٌ لكل عقد)."}
        </p>
      ) : (
        <div className="qb-projects">
          {mine.map((p, i) => (
            <button
              key={p.id}
              type="button"
              className="qb-card"
              style={{
                ["--qb-i" as string]: Math.min(i, 6),
                ["--qb-tone" as string]: p.color ?? "var(--brand)",
              }}
              onClick={() => navigate(`/agency/projects/${p.id}?from=account`)}
              title="افتح لوحة المشروع"
            >
              <span className="qb-title">{p.name}</span>
              {p.contract?.number && (
                <span className="tiny mute num">عقد {p.contract.number}</span>
              )}
              <span className="tiny mute">
                {p.doneTasks ?? p.done ?? 0} / {p.totalTasks ?? p.total ?? 0}{" "}
                مهمة منجزة
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   تبويب العقود — جدولٌ ببحثه، وصفُّ كل عقدٍ يحمل خطوته
   ========================================================================== */

function ContractsTab({
  accountId,
  contracts,
  onCreateProject,
}: {
  accountId: string;
  contracts: Array<Record<string, any>>;
  onCreateProject: (contractId: string) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  /* برفيو العقد = مستنده المطبوع نفسه — في مكانك كالفاتورة والكشف سواء. */
  const [previewing, setPreviewing] = useState<Record<string, any> | null>(
    null,
  );

  const shown = contracts.filter((c) =>
    matches(search, c.number, c.title, c.value, date(c.startAt)),
  );

  return (
    <div>
      <TabTools
        value={search}
        onChange={setSearch}
        placeholder="ابحث برقم العقد أو عنوانه أو قيمته"
      >
        {/* الإضافة تُبحر إلى العقود ونموذجُها مفتوحٌ والعميلُ منتقًى. */}
        <button
          className="btn btn-sm btn-primary"
          onClick={() =>
            navigate(`/sales/contracts?new=1&accountId=${accountId}`)
          }
        >
          <Icon name="plus" size={14} />
          إضافة عقد
        </button>
      </TabTools>

      <SimpleTable
        head={["الرقم", "الحالة", "القيمة", "البدء", "التوقيع", "المشروع", ""]}
        empty={search ? "لا عقد يطابق البحث." : "لا توجد عقود."}
        rows={shown.map((c) => [
          /* الرقم بارزًا وعنوانه همسًا بجانبه — كخانة الفاتورة سواء. */
          <span
            className="row"
            style={{ gap: 8, alignItems: "baseline", flexWrap: "wrap" }}
          >
            <Link
              to={`/sales/contracts/${c.id}?from=account`}
              className="num cell-strong"
            >
              {c.number}
            </Link>
            {c.title && <span className="tiny mute">{c.title}</span>}
          </span>,
          <Badge
            kind={
              CONTRACT_STATUS_MAP[c.status as keyof typeof CONTRACT_STATUS_MAP]
                ?.kind
            }
          >
            {CONTRACT_STATUS_MAP[c.status as keyof typeof CONTRACT_STATUS_MAP]
              ?.label ?? c.status}
          </Badge>,
          <span className="num">{money(c.value)}</span>,
          <span className="cell-mute">{date(c.startAt)}</span>,
          c.signedAt ? (
            <Badge kind="success">وُقّع</Badge>
          ) : (
            <span className="cell-mute">—</span>
          ),
          /* العقد الواحد مشروعٌ واحد: القائم رابطٌ، والسّاري بلا مشروعٍ زرُّ إنشاء. */
          c.agencyProjects?.[0] ? (
            <Link
              to={`/agency/projects/${c.agencyProjects[0].id}?from=account`}
              className="qb-chip is-ok"
            >
              <Icon name="rocket" size={11} />
              {c.agencyProjects[0].name}
            </Link>
          ) : c.status === "ACTIVE" ? (
            <button
              className="btn btn-sm"
              onClick={() => onCreateProject(c.id)}
              title="أنشئ مشروع تنفيذٍ لهذا العقد"
            >
              <Icon name="rocket" size={12} />
              أنشئ مشروعًا
            </button>
          ) : (
            <span
              className="cell-mute"
              title="المشروع يقوم على عقدٍ سارٍ — فعّل العقد أولًا"
            >
              —
            </span>
          ),
          /* برفيو في مكانك، وبجانبه نقاط العقد: توقيعٌ وتفعيلٌ وإنهاء. */
          <span className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
            <button
              className="btn btn-sm btn-icon"
              onClick={() => setPreviewing(c)}
              title="اطّلع على العقد هنا"
              aria-label={`اطّلع على العقد ${c.number}`}
            >
              <Icon name="eye" size={14} />
            </button>
            <ContractActions
              contract={
                c as {
                  id: string;
                  number: string;
                  status: string;
                  signedAt?: string | null;
                }
              }
              fromAccount
              onPreview={() => setPreviewing(c)}
              onChanged={() => {
                void queryClient.invalidateQueries({
                  queryKey: ["account", accountId],
                });
              }}
            />
          </span>,
        ])}
      />

      {previewing && (
        <DocPreview
          path={`/print/contract/${previewing.id}`}
          title={`عقد ${previewing.number}`}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   تبويب الفواتير — الإصدار عن صفقةٍ متعاقَدٍ عليها، والتحصيل من مكانه
   ========================================================================== */

function InvoicesTab({
  accountId,
  quotations,
  invoices,
  payments,
  onPreview,
  onPay,
}: {
  accountId: string;
  quotations: Array<Record<string, any>>;
  invoices: Array<Record<string, any>>;
  payments: Array<Record<string, any>>;
  onPreview: (invoiceId: string) => void;
  onPay: (invoice: Record<string, any>) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [collecting, setCollecting] = useState(false);
  /* سندات التحصيل: المدير يرى الكل، والمندوب ما سجّله هو وحده. */
  const { session, atLeast } = useAuth();
  const [filesFor, setFilesFor] = useState<Record<string, any> | null>(null);
  const ownsPayment = (pm: Record<string, any>) =>
    atLeast("MANAGER") || (!!session && pm.createdBy === session.user.id);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["account"] });
    void queryClient.invalidateQueries({ queryKey: ["invoices"] });
  };

  /*
   * حالة الفاتورة من صفّها: التعليم «مُرسَلة» يُثبت قيدها المحاسبي،
   * والإلغاء يعكسه — الخادم يحكم (مسودّةً كانت؟ سُدِّد جزءٌ منها؟)
   * ورسالته تُعرض كما هي. أمّا «مسدَّدة» فتأتي من التحصيلات لا من زرّ.
   */
  const sendInv = useMutation({
    mutationFn: (invId: string) =>
      api(`/finance/invoices/${invId}/send`, { method: "POST" }),
    onSuccess: () => {
      toast("عُلّمت مُرسَلة وأُثبت قيدها");
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الإرسال."),
  });
  const cancelInv = useMutation({
    mutationFn: (invId: string) =>
      api(`/finance/invoices/${invId}/cancel`, { method: "POST" }),
    onSuccess: () => {
      toast("أُلغيت الفاتورة وعُكس قيدها");
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الإلغاء."),
  });

  /*
   * أهلية الإصدار من هنا: عرضٌ مقبولٌ عقدُه موقَّعٌ سارٍ ولم تصدر عنه
   * فاتورة — فالفاتورة توثيقُ التزامٍ مكتمل لا قبولٍ شفهيّ، والخادم
   * يحرس القاعدة نفسها لو جاء الطلب من غير هذه النافذة.
   */
  const eligible = quotations.filter((q) => {
    const c = q.contracts?.[0];
    return (
      q.status === "ACCEPTED" &&
      c &&
      c.signedAt &&
      c.status === "ACTIVE" &&
      !q.invoices?.[0]
    );
  });

  const unpaid = invoices.filter(
    (i) =>
      i.status !== "CANCELLED" &&
      Number(i.paidAmount ?? 0) < Number(i.total ?? 0),
  );

  const issue = useMutation({
    mutationFn: (quotationId: string) => issueInvoiceFromQuotation(quotationId),
    onSuccess: (created) => {
      toast("صدرت الفاتورة — هذه معاينتها");
      setAdding(false);
      void queryClient.invalidateQueries({ queryKey: ["account"] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      onPreview(created.id);
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "تعذّر إصدار الفاتورة."),
  });

  const shown = invoices.filter((i) =>
    matches(
      search,
      i.number,
      i.total,
      date(i.dueAt),
      INVOICE_STATUS_MAP[i.status as keyof typeof INVOICE_STATUS_MAP]?.label,
    ),
  );

  return (
    <div>
      <TabTools
        value={search}
        onChange={setSearch}
        placeholder="ابحث برقم الفاتورة أو حالتها أو قيمتها"
      >
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setAdding(true)}
        >
          <Icon name="plus" size={14} />
          إضافة فاتورة
        </button>
        <button
          className="btn btn-sm"
          onClick={() => setCollecting(true)}
          disabled={unpaid.length === 0}
          title={
            unpaid.length ? "سجّل تحصيلًا على فاتورة" : "لا فواتير غير محصَّلة"
          }
        >
          <Icon name="wallet" size={14} />
          إضافة تحصيل
        </button>
      </TabTools>

      <SimpleTable
        head={["الرقم", "الحالة", "الإجمالي", "المحصّل", "الاستحقاق", ""]}
        empty={
          search
            ? "لا فاتورة تطابق البحث."
            : "لا فواتير بعد — تصدر من صفقةٍ متعاقَدٍ عليها بزرّ «إضافة فاتورة»."
        }
        rows={shown.map((i) => [
          /* الرقم وبجانبه عنوان عقده — القارئ يعرف عمّا صدرت بلا فتحها. */
          <span
            className="row"
            style={{ gap: 8, alignItems: "baseline", flexWrap: "wrap" }}
          >
            <Link
              to={`/finance/invoices/${i.id}?from=account`}
              className="num cell-strong"
            >
              {i.number}
            </Link>
            {(i.contract?.title || i.contract?.number) && (
              <span className="tiny mute">
                {i.contract.title ?? `عقد ${i.contract.number}`}
              </span>
            )}
          </span>,
          <Badge
            kind={
              INVOICE_STATUS_MAP[i.status as keyof typeof INVOICE_STATUS_MAP]
                ?.kind
            }
          >
            {INVOICE_STATUS_MAP[i.status as keyof typeof INVOICE_STATUS_MAP]
              ?.label ?? i.status}
          </Badge>,
          <span className="num cell-strong">{money(i.total)}</span>,
          <span className="num">{money(i.paidAmount)}</span>,
          <span className="cell-mute">{date(i.dueAt)}</span>,
          <span className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
            {/* برفيو في مكانك — لا إحالةَ إلى الحسابات لمجرّد النظر. */}
            <button
              className="btn btn-sm btn-icon"
              onClick={() => onPreview(i.id)}
              title="اطّلع على الفاتورة هنا"
              aria-label={`اطّلع على الفاتورة ${i.number}`}
            >
              <Icon name="eye" size={14} />
            </button>
            {/*
              التحصيل من مكانه: حصّلتَ من العميل فسجّل هنا —
              والدفعة تدخل الحسابات نفسها، لا دفترَ ظلٍّ.
            */}
            {Number(i.paidAmount ?? 0) < Number(i.total ?? 0) && (
              <button
                className="btn btn-sm"
                onClick={() => onPay(i)}
                title="سجّل تحصيلًا على هذه الفاتورة"
              >
                تحصيل
              </button>
            )}
            {/* نقاط الفاتورة: تعليمها مُرسَلة، فتحها، إلغاؤها — بأسبابها. */}
            <Menu
              label={`إجراءات الفاتورة ${i.number}`}
              actions={[
                {
                  key: "send",
                  label: "تعليمها مُرسَلة",
                  icon: "send",
                  onSelect: () => sendInv.mutate(i.id),
                  disabled: i.status !== "DRAFT" || sendInv.isPending,
                  title:
                    i.status === "DRAFT"
                      ? "تُثبت قيدها وتصير التزامًا على العميل"
                      : "تُعلَّم من المسودّة فقط — هذه أُرسلت أو أُغلقت",
                },
                {
                  key: "open",
                  label: "فتح في الحسابات",
                  icon: "receipt",
                  onSelect: () =>
                    navigate(`/finance/invoices/${i.id}?from=account`),
                },
                {
                  key: "cancel",
                  label: "إلغاء الفاتورة",
                  icon: "x",
                  danger: true,
                  separated: true,
                  onSelect: () => cancelInv.mutate(i.id),
                  disabled:
                    i.status === "CANCELLED" ||
                    Number(i.paidAmount ?? 0) > 0 ||
                    cancelInv.isPending,
                  title:
                    i.status === "CANCELLED"
                      ? "ملغاة بالفعل"
                      : Number(i.paidAmount ?? 0) > 0
                        ? "سُدِّد جزءٌ منها — اعكس التحصيل أولًا"
                        : "تُلغى ويُعكس قيدها — لا تُحذف",
                },
              ]}
            />
          </span>,
        ])}
      />

      <h3 className="label mb-4 mt-4">التحصيلات</h3>
      <SimpleTable
        head={["الرقم", "المبلغ", "الطريقة", "التاريخ", ""]}
        empty="لا توجد تحصيلات."
        rows={payments
          .filter((pm) =>
            matches(search, pm.number, pm.amount, pm.method, date(pm.paidAt)),
          )
          .map((pm) => [
            <span className="num">{pm.number}</span>,
            <span className="num">{money(pm.amount)}</span>,
            pm.method ?? "—",
            <span className="cell-mute">{date(pm.paidAt)}</span>,
            ownsPayment(pm) ? (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost"
                onClick={() => setFilesFor(pm)}
                aria-label={`سندات التحصيل ${pm.number}`}
                title="سند التحصيل"
              >
                <Icon name="paperclip" size={15} />
              </button>
            ) : null,
          ])}
      />

      {filesFor && (
        <Modal title={`سند التحصيل ${filesFor.number}`} onClose={() => setFilesFor(null)}>
          <Attachments entity="payment" entityId={filesFor.id} title="سند التحصيل" />
        </Modal>
      )}

      {/* الإصدار يختار صفقته ونوعه: كاملةٌ مرةً، أو دوريةٌ شهرًا بشهر. */}
      {adding && (
        <AddInvoiceModal
          accountId={accountId}
          eligible={eligible}
          issuing={issue.isPending}
          onIssueFull={(quotationId) => issue.mutate(quotationId)}
          onClose={() => setAdding(false)}
          onRecurringCreated={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}

      {/* التحصيل يختار فاتورته غير المحصَّلة — ثم نافذة التسجيل نفسها. */}
      {collecting && (
        <Modal title="تحصيل على فاتورة" onClose={() => setCollecting(false)}>
          <div className="pick-list">
            {unpaid.map((i) => (
              <button
                key={i.id}
                type="button"
                className="pick-row"
                onClick={() => {
                  setCollecting(false);
                  onPay(i);
                }}
              >
                <span className="pick-main">
                  <span style={{ fontWeight: 700 }} className="num">
                    {i.number}
                  </span>
                  <span className="tiny mute">
                    المتبقي{" "}
                    {money(Number(i.total ?? 0) - Number(i.paidAmount ?? 0))}
                  </span>
                </span>
                <span className="num cell-strong">{money(i.total)}</span>
                <Icon name="chevronLeft" size={14} />
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ==========================================================================
   إضافة فاتورة — كاملةٌ مرةً واحدة، أو دوريةٌ تصدر شهرًا بشهر
   ========================================================================== */

/**
 * الاختيار الأول نوعُ الفوترة لا الصفقة:
 *
 * «كاملة» تُصدر عن العرض فاتورةً واحدة بقيمته كلّها — المسار القائم.
 * «دورية» تُنشئ اشتراكًا على عقد الصفقة: قيمةٌ شهرية ويومُ إصدارٍ
 * ومدةٌ بالأشهر — فعقدُ تسويقٍ بعشرة آلاف شهريًّا لسنةٍ يُعرف من
 * لحظته أن اثنتي عشرة فاتورةً ستصدر، واحدةً كل شهر، يصنعها المجدول
 * في وقتها مسودّةً تُراجَع ثم تُعلَّم مُرسَلة — لا إصدارَ أعمى.
 */
function AddInvoiceModal({
  accountId,
  eligible,
  issuing,
  onIssueFull,
  onRecurringCreated,
  onClose,
}: {
  accountId: string;
  eligible: Array<Record<string, any>>;
  issuing: boolean;
  onIssueFull: (quotationId: string) => void;
  onRecurringCreated: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [kind, setKind] = useState<"FULL" | "RECURRING">("FULL");
  const [quotationId, setQuotationId] = useState(eligible[0]?.id ?? "");
  const picked = eligible.find((q) => q.id === quotationId) ?? null;

  const [monthly, setMonthly] = useState("");
  const [firstAt, setFirstAt] = useState(new Date().toISOString().slice(0, 10));
  const [months, setMonths] = useState("12");

  const amount = Number(monthly) || 0;
  const count = Math.max(1, Math.min(120, Math.floor(Number(months) || 0)));
  const taxRate = Number(picked?.taxRate ?? 0);
  const perInvoice = amount * (1 + taxRate / 100);
  const endDate = (() => {
    const d = new Date(`${firstAt}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return null;
    const e = new Date(d);
    e.setUTCMonth(e.getUTCMonth() + (count - 1));
    return e.toISOString().slice(0, 10);
  })();

  const createRecurring = useMutation({
    mutationFn: () =>
      api("/finance/recurring", {
        method: "POST",
        body: {
          accountId,
          contractId: picked?.contracts?.[0]?.id ?? null,
          title:
            `اشتراك شهري — ${picked?.title ?? picked?.number ?? ""}`.trim(),
          cycle: "MONTHLY",
          startDate: firstAt,
          endDate,
          dayOfMonth: Number(firstAt.slice(8, 10)),
          taxRate,
          lines: [
            {
              description: `دفعة شهرية — ${picked?.title ?? picked?.number ?? "العقد"}`,
              quantity: 1,
              unit: "شهر",
              unitPrice: amount,
            },
          ],
        },
      }),
    onSuccess: () => {
      toast(
        `أُنشئ الاشتراك — ستصدر ${count} فاتورة، واحدةً كل شهر بدءًا من ${date(firstAt)}`,
      );
      onRecurringCreated();
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "تعذّر إنشاء الاشتراك."),
  });

  const recValid = picked && amount > 0 && endDate && count >= 1;

  return (
    <Modal
      title="إضافة فاتورة"
      onClose={onClose}
      footer={
        kind === "RECURRING" ? (
          <>
            <button className="btn" onClick={onClose} type="button">
              إلغاء
            </button>
            <button
              className="btn btn-primary"
              disabled={!recValid || createRecurring.isPending}
              onClick={() => createRecurring.mutate()}
            >
              {createRecurring.isPending
                ? "جارٍ الإنشاء"
                : `إنشاء ${count} فاتورة شهرية`}
            </button>
          </>
        ) : undefined
      }
    >
      {/* نوع الفوترة أولًا — قرصان لا يلتبسان. */}
      <div className="row mb-4" style={{ gap: 6 }}>
        {(
          [
            ["FULL", "فاتورة كاملة", "receipt"],
            ["RECURRING", "فاتورة دورية", "refresh"],
          ] as Array<
            ["FULL" | "RECURRING", string, Parameters<typeof Icon>[0]["name"]]
          >
        ).map(([k, label, icon]) => (
          <button
            key={k}
            type="button"
            className={`btn btn-sm${kind === k ? " btn-primary" : ""}`}
            aria-pressed={kind === k}
            onClick={() => setKind(k)}
          >
            <Icon name={icon} size={13} />
            {label}
          </button>
        ))}
      </div>

      {eligible.length === 0 ? (
        <p className="mute small">
          لا صفقة جاهزة للفوترة: الفاتورة تصدر عن عرضٍ مقبولٍ عقدُه موقَّعٌ سارٍ
          ولم تصدر عنه فاتورة. أكمل السلسلة — موافقةٌ فعقدٌ يُوقَّع ويُفعَّل —
          ثم عُد إلى هنا.
        </p>
      ) : kind === "FULL" ? (
        <div className="pick-list">
          {eligible.map((q) => (
            <button
              key={q.id}
              type="button"
              className="pick-row"
              disabled={issuing}
              onClick={() => onIssueFull(q.id)}
            >
              <span className="pick-main">
                <span style={{ fontWeight: 700 }}>{q.title ?? q.number}</span>
                <span className="tiny mute num">
                  {q.number} — عقد {q.contracts[0].number}
                </span>
              </span>
              <span className="num cell-strong">{money(q.total)}</span>
              <Icon name="chevronLeft" size={14} />
            </button>
          ))}
        </div>
      ) : (
        <div className="form-grid">
          <div className="field span-2">
            <label className="label req" htmlFor="ri-deal">
              الصفقة (عقدها الموقَّع الساري)
            </label>
            <select
              id="ri-deal"
              className="select"
              value={quotationId}
              onChange={(e) => setQuotationId(e.target.value)}
            >
              {eligible.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.number}
                  {q.title ? ` — ${q.title}` : ""} (عقد {q.contracts[0].number})
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="label req" htmlFor="ri-amount">
              القيمة الشهرية
            </label>
            <input
              id="ri-amount"
              className="input num"
              type="number"
              min="0"
              step="any"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              placeholder="10000"
            />
          </div>

          <div className="field">
            <label className="label req" htmlFor="ri-first">
              تاريخ أول فاتورة
            </label>
            <input
              id="ri-first"
              className="input"
              type="date"
              value={firstAt}
              onChange={(e) => setFirstAt(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label req" htmlFor="ri-months">
              المدة بالأشهر
            </label>
            <input
              id="ri-months"
              className="input num"
              type="number"
              min="1"
              max="120"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            />
          </div>

          {/* الخلاصة قبل الالتزام: كم فاتورة، بكم، من متى إلى متى. */}
          {recValid && (
            <p className="small span-2" style={{ margin: 0 }}>
              ستصدر <strong className="num">{count}</strong> فاتورة — واحدةٌ يوم{" "}
              <span className="num">{Number(firstAt.slice(8, 10))}</span> من كل
              شهر، قيمة كلٍّ منها{" "}
              <strong className="num">{money(amount)}</strong>
              {taxRate > 0 && (
                <>
                  {" "}
                  + ضريبة {taxRate}% ={" "}
                  <strong className="num">{money(perInvoice)}</strong>
                </>
              )}
              ، من {date(firstAt)} حتى {endDate ? date(endDate) : "—"}. تُنشأ
              كلُّ فاتورةٍ في وقتها مسودّةً تُراجَع ثم تُعلَّم مُرسَلة.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ==========================================================================
   مشروعٌ من عقد — «اختر عقدًا نعمله مشروعًا»، والعقد الواحد مشروعٌ واحد
   ========================================================================== */

function ProjectFromContractModal({
  accountId,
  contracts,
  presetContractId,
  onClose,
}: {
  accountId: string;
  contracts: Array<Record<string, any>>;
  presetContractId?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  /* الأهلية: عقدٌ سارٍ بلا مشروعٍ قائم — فالعقد الواحد مشروعٌ واحد. */
  const eligible = useMemo(
    () =>
      contracts.filter((c) => c.status === "ACTIVE" && !c.agencyProjects?.[0]),
    [contracts],
  );

  const [contractId, setContractId] = useState(
    presetContractId ?? eligible[0]?.id ?? "",
  );
  const picked = eligible.find((c) => c.id === contractId) ?? null;
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(PROJECT_COLORS[0]);

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/agency/projects", {
        method: "POST",
        body: {
          kind: "CONTRACT",
          accountId,
          contractId,
          name: name.trim() || picked?.title || "مشروع تنفيذ العقد",
          color,
        },
      }),
    onSuccess: (created) => {
      toast("قام المشروع على عقده — هذه لوحته");
      void queryClient.invalidateQueries({ queryKey: ["agency"] });
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      onClose();
      navigate(`/agency/projects/${created.id}?from=account`);
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "تعذّر إنشاء المشروع."),
  });

  return (
    <Modal
      title="مشروع من عقد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={!contractId || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "جارٍ الإنشاء" : "إنشاء المشروع"}
          </button>
        </>
      }
    >
      {eligible.length === 0 ? (
        <p className="mute small">
          لا عقد جاهزًا: المشروع يقوم على عقدٍ سارٍ لم يُبنَ عليه مشروع — والعقد
          الواحد مشروعٌ واحد. واعلم أن تفعيل العقد يُنشئ مشروعه (الانضمام)
          آليًّا، فالغالب أن عقودك السارية مخدومةٌ بالفعل — تجد مشاريعها في هذا
          التبويب.
        </p>
      ) : (
        <div className="form-grid">
          <div className="field span-2">
            <label className="label req" htmlFor="pfc-contract">
              اختر عقدًا نعمله مشروعًا
            </label>
            <select
              id="pfc-contract"
              className="select"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
            >
              {eligible.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.number} — {c.title}
                </option>
              ))}
            </select>
            <p className="tiny mute">
              تُعرض العقود السارية التي لا مشروع لها فقط.
            </p>
          </div>

          <div className="field span-2">
            <label className="label" htmlFor="pfc-name">
              اسم المشروع
            </label>
            <input
              id="pfc-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={picked?.title ?? "مشروع تنفيذ العقد"}
            />
          </div>

          <div className="field span-2">
            <span className="label">لون اللوحة</span>
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="pfc-swatch"
                  style={{
                    background: c,
                    outline: color === c ? `2px solid ${c}` : undefined,
                  }}
                  aria-pressed={color === c}
                  aria-label={`اللون ${c}`}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ==========================================================================
   تسجيل تحصيل على فاتورة — من الملف إلى الحسابات مباشرةً
   ========================================================================== */

/**
 * حصّلتَ من العميل فسجّل حيث أنت: المبلغ يملؤه المتبقي على الفاتورة
 * ويقبل التعديل للدفعات الجزئية، والدفعة تُنشأ في الحسابات نفسها —
 * فتظهر في الفواتير والتحصيلات وكشوف الأشهر القادمة بلا خطوةٍ ثانية.
 */
function RecordPaymentModal({
  accountId,
  invoice,
  onClose,
  onDone,
}: {
  accountId: string;
  invoice: Record<string, any>;
  onClose: () => void;
  onDone: () => void;
}) {
  const remaining = Math.max(
    0,
    Number(invoice.total ?? 0) - Number(invoice.paidAmount ?? 0),
  );
  const [amount, setAmount] = useState(String(remaining));
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [cashAccountId, setCashAccountId] = useState("");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [fxRate, setFxRate] = useState("");

  /* مصادر التحصيل الحقيقية — خزائن النظام وحساباته البنكية نفسها. */
  const cashAccounts = useQuery({
    queryKey: ["cash-accounts", false],
    queryFn: () =>
      api<{
        items: Array<{ id: string; name: string; isDefault: boolean; currency?: string }>;
      }>("/finance/cash-accounts"),
  });
  /* تحصيل في خزينة بعملة أجنبية: المبلغ بعملتها، ويُخصم مقابله بالجنيه من الفاتورة. */
  const cashCurrency =
    cashAccounts.data?.items.find((c) => c.id === cashAccountId)?.currency ?? "EGP";
  const foreign = isForeignCurrency(cashCurrency);
  const rate = Number(fxRate) || 0;
  useEffect(() => {
    if (cashAccountId || !cashAccounts.data) return;
    const def =
      cashAccounts.data.items.find((c) => c.isDefault) ??
      cashAccounts.data.items[0];
    if (def) setCashAccountId(def.id);
  }, [cashAccounts.data, cashAccountId]);

  const save = useMutation({
    mutationFn: async () => {
      /*
       * الدفعة أولًا ثم الإيصال مرفقًا عليها: تُعلَّم SALES فتولد
       * «بانتظار الاعتماد» ويجدها المحاسب في تحصيلات فريق المبيعات.
       */
      const created = await api<{ id: string }>("/finance/payments", {
        method: "POST",
        body: {
          accountId,
          invoiceId: invoice.id,
          amount: foreign ? toBase(Number(amount), rate) : Number(amount),
          ...(foreign ? { fxAmount: Number(amount), fxRate: rate } : {}),
          method: method.trim() || undefined,
          reference: reference.trim() || undefined,
          cashAccountId: cashAccountId || undefined,
          recordedVia: "SALES",
        },
      });
      if (receipt) {
        const form = new FormData();
        form.append("file", receipt);
        form.append("entity", "payment");
        form.append("entityId", created.id);
        await upload("/attachments", form);
      }
      return created;
    },
    onSuccess: onDone,
  });

  const valid = Number(amount) > 0 && (!foreign || rate > 0);

  return (
    <Modal
      title={`تحصيل — فاتورة ${invoice.number}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "جارٍ التسجيل" : "سجّل التحصيل"}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-3">
          {save.error instanceof Error ? save.error.message : "تعذّر التسجيل."}
        </div>
      )}

      <p className="tiny mute mb-3">
        إجمالي الفاتورة <span className="num">{money(invoice.total)}</span> —
        حُصِّل منها <span className="num">{money(invoice.paidAmount)}</span>{" "}
        والمتبقي <span className="num">{money(remaining)}</span>. تدخل الحسابات
        فورًا «بانتظار الاعتماد» — يعتمدها المحاسب بعد التأكد من وصولها.
      </p>

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="rp-amount">
            المبلغ المحصَّل{foreign ? ` (${currencySymbol(cashCurrency)})` : ""}
          </label>
          <input
            id="rp-amount"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="rp-cash">
            مصدر التحصيل
          </label>
          <select
            id="rp-cash"
            className="select"
            value={cashAccountId}
            onChange={(e) => setCashAccountId(e.target.value)}
          >
            {(cashAccounts.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {isForeignCurrency(c.currency) ? ` (${c.currency})` : ""}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            خزائن النظام وحساباته البنكية نفسها.
          </span>
        </div>
        {foreign && (
          <FxRateField
            id="rp-rate"
            currency={cashCurrency}
            rate={fxRate}
            onRate={setFxRate}
            fxTotal={Number(amount) || 0}
          />
        )}
        <div className="field">
          <label className="label" htmlFor="rp-method">
            طريقة الدفع
          </label>
          <input
            id="rp-method"
            className="input"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            placeholder="نقدًا، تحويل بنكي، شيك…"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="rp-ref">
            مرجع (اختياري)
          </label>
          <input
            id="rp-ref"
            className="input num"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="رقم التحويل أو الشيك"
          />
        </div>
        <div className="field span-2">
          <label className="label" htmlFor="rp-receipt">
            صورة الإيصال (اختياري)
          </label>
          <input
            id="rp-receipt"
            className="input"
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
          />
          <span className="tiny mute">
            إيصال التحويل أو صورة الشيك — يُرفق على الدفعة ويراه المحاسب عند
            الاعتماد.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   كشوف الحساب الشهرية — يُصدرها النظام آليًّا
   ========================================================================== */

const MONTHS_AR = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

function AccountStatements({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const [previewing, setPreviewing] = useState<Record<string, any> | null>(
    null,
  );
  const [search, setSearch] = useState("");

  const statements = useQuery({
    queryKey: ["account", accountId, "statements"],
    queryFn: () =>
      api<Array<Record<string, any>>>(`/crm/accounts/${accountId}/statements`),
  });

  /* إصدارٌ بأثر رجعي — للمشرف حين لا يريد انتظار مواعيد الإصدار. */
  const generate = useMutation({
    mutationFn: () =>
      api<{ issued: number }>(
        `/crm/accounts/${accountId}/statements/generate`,
        {
          method: "POST",
          body: { months: 6 },
        },
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ["account", accountId, "statements"],
      }),
  });

  const rows = (statements.data ?? []).filter((st) =>
    matches(
      search,
      MONTHS_AR[(st.periodMonth ?? 1) - 1],
      st.periodYear,
      st.balance,
      date(st.issuedAt),
    ),
  );

  return (
    <div>
      <p className="tiny mute mb-3">
        يُصدر النظام كشفًا لكل شهرٍ آليًّا — آخرَ يومٍ من الشهر، أو في اليوم
        المضبوط في «الإعدادات ← بيانات الشركة». الكشف وثيقةُ لحظته: أرقامه تُحسب
        وتُخزَّن ولا تتغيّر بأثرٍ رجعي.
      </p>

      <TabTools
        value={search}
        onChange={setSearch}
        placeholder="ابحث بالشهر أو السنة"
      >
        {/* الكشف التفصيلي لأيّ مدة: كل فاتورة ودفعة برصيدها — من دفتر الأستاذ. */}
        {atLeast("MANAGER") && (
          <Link
            to={statementPath("customer", accountId)}
            className="btn btn-sm btn-primary"
          >
            <Icon name="receipt" size={14} />
            كشف حساب تفصيلي
          </Link>
        )}
        {atLeast("ADMIN") && (
          <button
            className="btn btn-sm"
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending
              ? "جارٍ الإصدار…"
              : "أصدر كشوف الأشهر الستة الماضية الآن"}
          </button>
        )}
      </TabTools>

      {generate.isError && (
        <div className="alert alert-danger mb-3">
          {generate.error instanceof Error
            ? generate.error.message
            : "تعذّر الإصدار."}
        </div>
      )}

      {statements.isPending && (
        <div className="skeleton" style={{ height: 90 }} />
      )}

      {!statements.isPending && rows.length === 0 && (
        <p className="mute small">
          {search
            ? "لا كشف يطابق البحث."
            : "لا كشوف بعد — تصدر آليًّا في موعدها، أو أصدرها المشرف الآن."}
        </p>
      )}

      {rows.length > 0 && (
        <SimpleTable
          head={[
            "الشهر",
            "إجمالي التعاقدات",
            "المفوتر",
            "المدفوعات",
            "المتبقي",
            "صدر في",
            "",
          ]}
          empty=""
          rows={rows.map((st) => [
            <span className="cell-strong">
              {MONTHS_AR[(st.periodMonth ?? 1) - 1]}{" "}
              <span className="num">{st.periodYear}</span>
            </span>,
            <span className="num">{money(st.contractedTotal)}</span>,
            <span className="num">
              {money(st.invoicedTotal)}
              <span className="tiny mute"> (+{money(st.invoicedInMonth)})</span>
            </span>,
            <span className="num">
              {money(st.paidTotal)}
              <span className="tiny mute"> (+{money(st.paidInMonth)})</span>
            </span>,
            <span
              className="num"
              style={{
                color: Number(st.balance) > 0 ? "var(--danger)" : undefined,
              }}
            >
              {money(st.balance)}
            </span>,
            <span className="cell-mute">{date(st.issuedAt)}</span>,
            <button
              className="btn btn-sm btn-icon"
              onClick={() => setPreviewing(st)}
              title="اطّلع على كشف الحساب"
              aria-label="اطّلع على كشف الحساب"
            >
              <Icon name="eye" size={14} />
            </button>,
          ])}
        />
      )}

      {/* برفيو الكشف = مستنده المطبوع نفسه — بترويسة الشركة وهويتها. */}
      {previewing && (
        <DocPreview
          path={`/print/statement/${accountId}/${previewing.id}`}
          title="معاينة كشف الحساب"
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

function SimpleTable({
  head,
  rows,
  empty,
}: {
  head: string[];
  rows: React.ReactNode[][];
  empty: string;
}) {
  if (rows.length === 0) return <p className="mute small">{empty}</p>;

  return (
    <div className="table-wrap">
      {/* رأسٌ مظلَّل بخطّه الثقيل، وصفوفٌ تدخل متتابعةً فيُرى أنها تبدّلت. */}
      <table className="table acct-tbl">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, r) => (
            <tr key={r} style={{ ["--row" as string]: Math.min(r, 10) }}>
              {cells.map((cell, c) => (
                <td key={c}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * حالة العميل الائتمانية.
 *
 * تحت الملخّص المالي مباشرةً: السؤال الذي يليه هو «وهل نبيع له أكثر؟»،
 * وهذه اللوحة جوابه.
 */
function CreditPanel({
  accountId,
  currentLimit,
}: {
  accountId: string;
  currentLimit: number | null;
}) {
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const credit = useCredit(accountId);

  /*
   * تحرير السقف هنا لا في نموذج بيانات العميل.
   *
   * السقف رقم مالي يُراجَع أمام رصيد العميل، لا حقلٌ يُملأ مع العنوان
   * والهاتف. ووضعه بجوار المستحق يجعل القرار مبنيًا على ما يراه من يقرّر.
   */
  const [limit, setLimit] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (dirty) return;
    setLimit(currentLimit == null ? "" : String(currentLimit));
  }, [currentLimit, dirty]);

  const saveLimit = useMutation({
    mutationFn: () =>
      api(`/crm/accounts/${accountId}`, {
        method: "PATCH",
        /* الفراغ يعني «ارجع إلى افتراضي الشركة» لا صفرًا. */
        body: { creditLimit: limit.trim() === "" ? null : Number(limit) },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["credit"] });
      await queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      setDirty(false);
    },
  });

  const hold = useMutation({
    mutationFn: (next: boolean) =>
      api(`/finance/credit/accounts/${accountId}/hold`, {
        method: "POST",
        body: { hold: next },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["credit"] });
    },
  });

  if (!credit.data) return null;
  const held = credit.data.status === "HOLD";

  return (
    /*
     * لوحةٌ مضغوطة في العمود الضيّق، لا بطاقة بعرض الصفحة.
     *
     * كانت تشغل عرض الشاشة كله لثلاثة عناصر: شريط، وحقل، وزرّ — فتدفع سجلّ
     * التعامل شاشةً كاملة إلى أسفل. وهي جواب سؤالٍ واحد: «هل نبيع له
     * أكثر؟» — يُقرأ بجوار بياناته لا قبل يوميّاته.
     */
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">حد الائتمان</h2>
        {atLeast("ADMIN") && (
          <button
            className={`btn btn-sm ${held ? "" : "btn-danger"}`}
            onClick={() => hold.mutate(!held)}
            disabled={hold.isPending}
            title={
              held
                ? "يعيد السماح بالبيع الآجل"
                : "يمنع أي إصدار آجل جديد لهذا العميل"
            }
          >
            {held ? "رفع الإيقاف" : "إيقاف الآجل"}
          </button>
        )}
      </div>

      <div className="card-body">
        <CreditBar credit={credit.data} />

        {atLeast("ADMIN") && (
          <div
            className="row mt-3"
            style={{ gap: "var(--sp-2)", alignItems: "flex-end" }}
          >
            <div className="field" style={{ flex: 1, minWidth: 0 }}>
              <label className="label" htmlFor="acc-limit">
                سقف هذا العميل
              </label>
              <input
                id="acc-limit"
                className="input num"
                type="number"
                min="0"
                step="1000"
                placeholder="افتراضي الشركة"
                value={limit}
                onChange={(e) => {
                  setLimit(e.target.value);
                  setDirty(true);
                }}
              />
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => saveLimit.mutate()}
              disabled={!dirty || saveLimit.isPending}
            >
              {saveLimit.isPending ? "جارٍ الحفظ" : "حفظ"}
            </button>
          </div>
        )}

        {atLeast("ADMIN") && (
          <div className="tiny mute mt-2">
            فارغًا يأخذ افتراضي الشركة، وصفرٌ يجعله نقديًا فقط.
          </div>
        )}

        {saveLimit.isError && (
          <div className="alert alert-danger mt-4">
            {saveLimit.error instanceof Error
              ? saveLimit.error.message
              : "تعذّر الحفظ."}
          </div>
        )}

        {hold.isError && (
          <div className="alert alert-danger mt-4">
            {hold.error instanceof Error
              ? hold.error.message
              : "تعذّرت العملية."}
          </div>
        )}

        {credit.data.enforcement === "OFF" && (
          <div className="tiny mute mt-2">
            الإلزام معطّل على مستوى الشركة: هذه الأرقام للاطلاع ولا يعترض النظام
            على إصدار.
          </div>
        )}
      </div>
    </div>
  );
}

/** عرض قيمة حقل مخصَّص نصًّا. */
function formatValue(type: string, raw: unknown): string {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (type === "BOOLEAN") return raw === true ? "نعم" : "لا";
  return String(raw);
}
